/**
 * Zuhause — nach Hause kommen. Vespera, kein Dokument.
 * Erster Blick: Gruß, Heute-Blatt, zwei Offen-Karten, Sprechen.
 * 2026-09-03: Der „Mehr"-Block (Merksatz, Schnellfragen, „Gesagt") ist weg —
 * gemessen 13 Tipps in vier Monaten. Die Beispielfragen leben im Onboarding.
 */
import { baerliStreifen } from "./baerli.js";
import { nutzerScrolltGerade } from "./scroll-ruhe.js";
import { echterAnschluss, schalteDurchgehend } from "./alltag-steuern.js";
import { starteSichtbarenTakt } from "./abfrage-takt.js";
import { gesteuert, sanft } from "./ruckel-schalter.js";
import { PULT_ZEICHEN, bauePult, setzePult } from "./steuerpult.js";
import { zeichen, tugendZeichenName, wegZeichenName } from "./zeichen.js";
import { stufeFuer, smileySvg, verfassungsZeile } from "./verfassung.js";
import { renderMarkdown } from "./markdown.js";
import { fetchAlltag, fetchAlltagStatus } from "./alltag-api.js";
import { fetchChatHistory } from "./chat-history-api.js";
import { openTicketsModal } from "./tickets-view.js";
import { gemerkteSonne } from "./stern.js";
import {
  baueZuhauseLeisten,
  formatUhr,
  formatDatum,
  gruss,
  tagesblattTitel,
  vorname,
  wienTagSchluessel,
  zuhauseTelefonTun,
  zuhauseWeitereSatz,
  type ZuhauseZeile,
  istZuhauseTun,
} from "./zuhause.js";
import { haengeMikrofonAn } from "./feld-mikrofon.js";
import { wachsendesTextfeld } from "./sheet-ui.js";
import {
  handlungFuerZeile,
  istErinnerungTitel,
  istOffenFaden,
  teileOffene,
  titelFuerAnzeige,
} from "./tickets-offen.js";
import { zeichen as zeichenMarkup } from "./zeichen.js";
import { tugendDesTages, ohneQuelle } from "../../shared/tugenden.js";
import {
  bodenStreckung,
  mondName,
  mondphase,
  morgenroete,
  tagesszene,
  tageszeitJetzt,
  wetterlageAus,
  type Wetterlage,
  rueckeGestirn,
} from "./tagesszene.js";
import { holeZuhauseLage, zeichneZuhauseLage } from "./zuhause-lage.js";
import { loadSettings } from "./settings.js";
import { jahreszeitFuer } from "../../shared/jahreszeit.js";
import {
  meditationHeute,
  waehleNebenbei,
  wienerTag,
  DANKBAR_NACHRICHT,
  type AngebotDaten,
  type Angebot,
} from "./tagesangebot.js";
import { avatarUrlAus, fetchPersona } from "./persona-api.js";
import { istHauptnutzer } from "./hauptnutzer.js";
import { nimmWillkommen } from "./zuhause-willkommen.js";
import { baueNeuReiter, type NeuReiter } from "./neu-reiter.js";
import { baueWitzKarte, type WitzKarte } from "./witz-karte.js";
import { laufText, pauseAnzeige, pauseText } from "./pause-besitzer.js";

const WETTERLAGEN = new Set<Wetterlage>([
  "regen",
  "sonne",
  "wolken",
  "unbekannt",
]);

const GERUEST_KEY = "vespera.zuhause.stand";

/** Vitest setzt process — im Browser gibt es process nicht. */
const IM_TEST =
  typeof process !== "undefined" && Boolean(process.env?.VITEST);

interface AnkerMoment {
  id: string;
  bildUrl: string;
  gefuehl: string;
  situation: string;
  ort: string | null;
  erlebtAm: string | null;
}
interface AnkerAntwort {
  ok?: boolean;
  gesamt?: number;
  anker?: AnkerMoment | null;
}

/** Die drei Bereiche der Zuhause-Seite (23.09.2026). */
/** Nach einem leeren Fund: so lange warten, bis erneut gefragt wird (Ticket 2561). */
export const FUND_NEU_NACH_MS = 15 * 60_000;
/** Höchstens so viele Nachfragen je Tag — jede kann einen Lauf beim starken Modell auslösen. */
export const FUND_NEU_HOECHSTENS = 3;

type ZuhauseBereich = "heute" | "tag" | "rueckblick" | "neu";

export class ZuhauseView {
  private root: HTMLElement;
  private laden = false;

  private letzteTun: ZuhauseZeile[] = [];
  private fuelleSperre = false;
  private warteDaten = true;

  /**
   * Die Körper-Karte, damit sie sich erneuern kann. (09.09.2026)
   *
   * Martin: *„Meine Verfassung steht noch auf vierzig Prozent, obwohl Körper
   * 67 Prozent ist im Whoop. Das sollte natürlich auch immer live
   * aktualisiert werden, damit ich den aktuellen Wert sehe."*
   *
   * ⚠️ `blickKoerper()` läuft EINMAL beim Seitenaufbau und ruft `fuelleKoerper`
   *    genau einmal. Danach nie wieder. Whoop rechnet die Erholung des neuen
   *    Tages aber erst im Lauf des Vormittags aus — bis dahin liefert
   *    `/api/whoop/summary?days=1` noch GESTERN. Nachgemessen am 09.09.:
   *    gestern 40 %, heute 67 %. Wer die Seite morgens offen hatte, sah den
   *    ganzen Tag 40 % und dazu „Whoop ist noch nicht aktualisiert" — beides
   *    richtig zum Zeitpunkt des Ladens und beides längst überholt.
   */
  private koerperSec: HTMLElement | null = null;
  private koerperKarte: HTMLElement | null = null;
  /**
   * Das eine Angebot in „Nebenbei" (Ticket 2561): was die reine Wahl
   * (lib/tagesangebot.ts) braucht, und welches Bild gerade steht.
   * ⚠️ EIN Besitzer: nur zeigeAngebot() setzt Inhalt und `hidden` der Stelle
   *    und des Fund-Hinweises. Meditation und Fund liefern nur Daten.
   */
  private angebotDaten: AngebotDaten = { meditation: null, fund: null, gestartet: null };
  private angebotSchluessel = "";
  private fundHinweisTitel: string | null = null;
  /** Für welchen Wiener Tag der Fund zuletzt geholt wurde. */
  private fundGeholtFuer = "";
  /**
   * Nach einem leeren Fund: ab wann wieder gefragt wird, und wie oft heute
   * schon. (24.09.2026, Prüfbefund zu 2561) Die Route antwortet auch dann
   * mit null, wenn der Fund GERADE entsteht (zweites Gerät offen) — ein
   * Tag Sperre ließ die Stelle dann bis Mitternacht leer. Nach einem
   * gescheiterten Modell-Lauf fragt der Server das starke Modell aber
   * erneut; darum höchstens FUND_NEU_HOECHSTENS Nachfragen je Tag.
   */
  private fundWiederAb = 0;
  private fundNachfragen = { tag: "", zahl: 0 };

  /**
   * ⚠️ Hier steht seit 17.09. die ANHALTE-Funktion, nicht mehr eine
   *    Timer-Kennung: `starteSichtbarenTakt` gibt einen Abschalter zurueck.
   *    Ein `clearInterval` darauf waere still wirkungslos.
   */
  private lageTakt: (() => void) | null = null;
  /*
   * 2026-09-04 (Martin, mit Bild: Uhr zeigt 21:49, die Seite steht auf
   * 18:42): *„Das Zuhause ist nicht aktualisiert. Das sollte natürlich
   * synchron mit der Uhrzeit sein und mit allen anderen Parametern."*
   *
   * `refresh()` lief nur beim Öffnen der Seite und beim Wechsel dorthin. Wer
   * schon dort steht und die App wegschiebt, kam auf einen drei Stunden
   * alten Stand zurück — Uhrzeit, Gruß, Sonnenstand, Wetter, alles von
   * damals.
   *
   * ⚠️ `refresh()` macht SECHS Serveranfragen. Es jede Minute laufen zu
   *    lassen wäre die naheliegende und falsche Lösung. Was jede Minute
   *    altert — Uhr, Gruß, Sonnenstand — lässt sich ohne eine einzige
   *    Anfrage erneuern; dafür wird der letzte Wetterstand gemerkt.
   */
  private letztesWetter: WetterBlick | null = null;
  private uhrTakt: ReturnType<typeof setInterval> | null = null;

  constructor(root: HTMLElement) {
    this.root = root;
    this.root.classList.add("zuhause");
    this.baueGeruest();
    this.horcheTelefon();
    this.zeigeAltenStand();
    this.starteLage();
    this.horcheAvatar();
    window.addEventListener("voice-v8:tiefe-frage-beantwortet", () => this.erneuereTagesbild());
    /*
     * 07.09.2026 — „Dein Tag" geht sofort mit.
     *
     * Martin: *„Ich habe jetzt das eine Häkchen gemacht … dann sollte, ohne
     * dass ich die Seite neu laden muss, der Progress sichtbar sein in Dein
     * Tag."*
     *
     * ⚠️ EIN Ereignis für alle, die etwas abhaken — statt an jeder Stelle
     *    einen eigenen Weg zurück zum Tagesbild. Das gab es für die tiefe
     *    Frage schon (Zeile darüber); jeder weitere Haken hätte sonst seine
     *    eigene Verdrahtung bekommen, und der nächste wäre wieder vergessen
     *    worden — genau so ist es beim Impuls passiert.
     */
    window.addEventListener("voice-v8:tagesbild-frisch", () =>
      this.erneuereTagesbild(),
    );
    /*
     * 07.09.2026 — die beantwortete Frage hakt sich selbst ab.
     *
     * Martin: *„Ich habe gerade die Frage beantwortet. Die könnte dann auch
     * automatisch, wenn sie noch dort steht, als abgehakt quittiert
     * werden."*
     *
     * ⚠️ Serverseitig war sie das längst — `/api/offen` liefert die Zeile
     *    gar nicht mehr, sobald die Frage beantwortet ist. Sie stand nur
     *    noch da, weil niemand die Liste neu geholt hat. Genau derselbe
     *    Fehler wie beim Impuls-Punkt eine Stunde vorher: das Ereignis war
     *    da, der Zuhörer fehlte.
     */
    window.addEventListener("voice-v8:tiefe-frage-beantwortet", () => {
      this.streicheOffen("frage:");
    });
    window.addEventListener("voice-v8:settings-changed", this.beiJahreszeitGewechselt);
    void this.refresh();
  }

  /**
   * Die Jahreszeit der Szene geht sofort mit. (07.10.2026)
   *
   * Einstellungen → Darstellung → „Jahreszeit der Szene", hier oder auf
   * einem anderen Gerät (der Abgleich meldet dasselbe Ereignis). Gezeichnet
   * wird über den gewohnten Weg: der Schlüssel des Neuaufbaus trägt die
   * Jahreszeit — ändert sie sich nicht, baut nichts neu.
   */
  private beiJahreszeitGewechselt = (ev: Event): void => {
    const felder = (ev as CustomEvent<{ felder?: string[] }>).detail?.felder;
    if (Array.isArray(felder) && !felder.includes("szeneJahreszeit")) return;
    try {
      if (this.letztesWetter) {
        this.zeigeSzeneUndKachel(this.letztesWetter);
        return;
      }
      const z = this.szeneZuletzt;
      if (z) this.fuelleSzene(z.lage, z.zeit, z.halter, z.sonne);
    } catch {
      /* Ein Bild, das nicht nachzieht, darf Zuhause nicht kosten. */
    }
  };

  /** 600px — kein User-Agent. Nur die Grenze, nicht jedes Pixel. */
  private horcheTelefon(): void {
    if (typeof matchMedia !== "function") return;
    matchMedia("(max-width: 600px)").addEventListener("change", () => {
      if (this.fuelleSperre || this.warteDaten) return;
      this.fuelleOffen(this.letzteTun);
    });
  }

  async refresh(): Promise<void> {
    // ⚠️ Nicht nochmal starten, während der erste Lauf läuft. goToView ruft
    // refresh, der Constructor auch — ein zweiter Lauf hat 2474 die Uhr
    // hingeschrieben und Wetter/Heute/Offen wieder geleert.
    if (this.laden) return;
    this.laden = true;
    // Das Tagesbild altert wie alles andere — nur beim ersten Aufbau
    // geholt, blieb „Frage" offen, obwohl sie beantwortet war.
    if (!this.warteDaten) this.erneuereTagesbild();
    /*
     * Die Körper-Karte altert wie alles andere. ⚠️ Bewusst HIER und nicht im
     * Minutentakt: Eine Whoop-Abfrage geht über den Server nach draußen, und
     * die Erholung ändert sich einmal am Tag, nicht jede Minute. `refresh()`
     * läuft beim Öffnen, beim Wechsel hierher und immer, wenn die App wieder
     * sichtbar wird (`beiLageSichtbar`) — also genau dann, wenn Martin
     * hinsieht.
     */
    if (this.koerperSec && this.koerperKarte) {
      void this.fuelleKoerper(this.koerperSec, this.koerperKarte);
    }
    // Ticket 2549/2561: Wer die App über Nacht offen hatte, sieht die
    // fertige Morgenmeditation beim ersten Hinsehen — nicht erst nach
    // Neuladen. Der Fund wird je Wiener Tag einmal geholt.
    void this.fuelleMeditation();
    void this.witzKarte.lade();
    if (this.fundGeholtFuer !== wienerTag(new Date()) && Date.now() >= this.fundWiederAb) {
      void this.fuelleFund();
    }
    const geruestWeg = window.setTimeout(() => {
      if (this.warteDaten) this.nimmGeruestWeg();
    }, 1500);
    try {
      const wetterP = holeWetterJetzt().catch(() => null);
      void wetterP.then((w) => {
        if (!w) return;
        this.letztesWetter = w;
        this.zeigeSzeneUndKachel(w);
        this.zeigeMeta(`${formatUhr()}  ·  ${w.zeile}`, true);
      });
      const [alltag, hist, tickets, wetter, status, offen] = await Promise.all([
        fetchAlltag().catch(() => null),
        fetchChatHistory({ limit: 20 }).catch(() => []),
        holeOffeneTickets().catch(() => []),
        wetterP,
        fetchAlltagStatus(14).catch(() => null),
        holeOffeneSachen().catch(() => []),
      ]);
      this.zeigeGruss();
      if (!IM_TEST) this.zeigeWillkommen();
      const heute = alltag?.days?.[0];
      const heuteTitel =
        heute?.rollup?.trim() ||
        heute?.entries?.[0]?.summary?.trim() ||
        heute?.entries?.[0]?.title ||
        null;
      const gesagt = hist
        .filter((m) => m.role === "user" && m.content.trim())
        .slice(0, 16)
        .map((m) => ({ id: m.id, text: m.content }));
      const uhr = formatUhr();
      const leisten = baueZuhauseLeisten({
        uhr,
        wetter: wetter?.zeile ?? null,
        heuteTitel,
        heuteNeben: heute?.dayKey ?? null,
        gesagt,
        tun: tickets.map((t) => ({
          id: String(t.id),
          /*
           * 2026-09-05 (Martin): *„Wenn ich etwas nicht bestätige, wie die
           * Klimmzüge, sollte der Topf mehr werden … dass dann dort zwei
           * steht."*
           *
           * Seit dem 02.09. weicht die alte Meldung der neuen — sonst
           * standen 56 Klimmzug-Zeilen in der Liste. Damit verschwand aber
           * auch, wie oft er es liegen gelassen hat: Zwei übersprungene Tage
           * sahen aus wie der erste.
           *
           * Die Zahl steht jetzt an der Zeile. Ab dem zweiten Mal — beim
           * ersten wäre sie nur Lärm.
           */
          titel:
            typeof t.serieZahl === "number" && t.serieZahl > 1
              ? `${t.title} · ${t.serieZahl}×`
              : t.title,
          kind: t.kind ?? null,
          createdAt: t.createdAt ?? null,
        })),
        offen,
      });
      if (wetter) this.zeigeSzeneUndKachel(wetter);
      const meta = leisten.heute.find((z) => z.art === "meta");
      const karten = leisten.heute.filter((z) => z.art !== "meta");
      this.zeigeBlattTitel(
        tagesblattTitel(heute?.dayKey ?? null, wienTagSchluessel()),
      );
      const letzte = heute?.entries?.[0] ?? null;
      this.fuelleZuletzt(
        letzte?.title?.trim() || letzte?.summary?.trim() || null,
        heute?.dayKey
          ? tagesblattTitel(heute.dayKey, wienTagSchluessel())
          : null,
      );
      // Wetter nicht mit der nackten Uhr überschreiben, wenn der Abruf
      // scheiterte — die Zeile von vorhin bleibt.
      if (wetter || !this.root.querySelector("[data-slot=meta]")?.textContent?.includes("°")) {
        // ⚠️ Auch hier mit Mond: Diese Stelle setzt die WETTERZEILE (aus der
        //    Übersicht), nicht den Aktiv-Text — ich hatte sie beim ersten
        //    Anlauf falsch eingeordnet und sie überschrieb die Zeile
        //    anschliessend ohne Mondphase.
        this.zeigeMeta(meta?.titel ?? "", true);
      }
      if (alltag || karten.length) {
        this.fuelleHeute(karten);
      }
      // 06.09.: Die Zusammenfassung kam bisher NUR aus dem Zwischenspeicher
      // der letzten Ladung — live wurde sie berechnet, aber nie gezeigt.
      /*
       * ⚠️ 23.09.2026 — „Dein Tag bisher" (Mein Tag) zeigt nur HEUTE. Ein
       *    Vortag stand hier bisher als „Gestern" — seit es den Rückblick
       *    gibt, stünde er dann zweimal da. Er gehört dorthin.
       */
      const heuteSchluessel = wienTagSchluessel();
      if (heuteTitel && heute?.dayKey === heuteSchluessel) {
        this.zeigeTagesblatt(heuteTitel);
      } else {
        const blattSec = this.root.querySelector<HTMLElement>('[data-leiste="gestern"]');
        if (blattSec) blattSec.hidden = true;
      }
      const gesternSchluessel = wienTagSchluessel(
        new Date(Date.now() - 86_400_000),
      );
      const vortag =
        alltag?.days?.find((d) => d.dayKey === gesternSchluessel) ?? null;
      this.zeigeVortag(
        vortag?.dayKey ?? null,
        vortag?.rollup?.trim() || vortag?.entries?.[0]?.summary?.trim() || null,
      );
      this.letzteTun = leisten.tun;
      this.fuelleOffen(leisten.tun);
      this.merkeStand(
        heuteTitel,
        leisten.tun,
        wetterlageAus(wetter?.beschreibung),
        wetter?.zeile ?? null,
        wetter?.sonne ?? null,
        // 07.09.: ohne die Zahl springt das Bild beim naechsten Laden von
        // Sonne auf Wolken — siehe merkeStand.
        wetter?.bewoelkung ?? null,
      );
      const heuteKey = wienTagSchluessel();
      const tage = status?.days ?? [];
      const heuteN =
        tage.find((d) => d.date === heuteKey)?.recordings ??
        status?.now?.todayEntries ??
        0;
    } finally {
      window.clearTimeout(geruestWeg);
      this.warteDaten = false;
      this.laden = false;
      const blick = this.root.querySelector<HTMLElement>(".zuhause-blick");
      if (blick) {
        delete blick.dataset.pending;
        blick.removeAttribute("aria-busy");
      }
      this.nimmGeruestWeg();
      void this.fuelleLage();
    }
  }

  private baueGeruest(): void {
    this.root.replaceChildren();
    const name =
      document.getElementById("nav-user-name")?.textContent?.trim() || "";
    const kopf = document.createElement("header");
    kopf.className = "zuhause-kopf";
    const avatar = document.createElement("button");
    avatar.type = "button";
    avatar.className = "zuhause-avatar";
    avatar.dataset.slot = "avatar";
    avatar.hidden = true;
    avatar.title = "Persönlichkeit";
    avatar.setAttribute("aria-label", "Persönlichkeit — Avatar wählen");
    const avatarImg = document.createElement("img");
    avatarImg.alt = "";
    avatarImg.addEventListener("error", () => {
      avatar.hidden = true;
    });
    avatar.append(avatarImg);
    avatar.addEventListener("click", () => {
      document.dispatchEvent(
        // Der Avatar wohnt auf der Seite „Persönlichkeit" (seit 23.09. eine
        // eigene Seite statt des Reiters „Ich").
        new CustomEvent("vespera:go-settings", {
          detail: { sub: "persoenlichkeit" },
        }),
      );
    });
    const hallo = document.createElement("p");
    hallo.className = "zuhause-gruss";
    hallo.dataset.slot = "gruss";
    kopf.append(avatar, hallo);
    // ⚠️ Gefuellt wird er ERST nach dem Zusammensetzen: `zeigeGruss` sucht
    // ueber `this.root`, und `kopf` haengt hier noch nicht darin.
    void this.fuelleAvatar();

    const blick = document.createElement("div");
    blick.className = "zuhause-blick";
    blick.dataset.pending = "1";
    blick.setAttribute("aria-busy", "true");
    /*
     * ⚠️⚠️ 23.09.2026 — DREI REITER STATT ZWEIER SPALTEN.
     *
     * Martin, mit Bild vom Rechner: *„Ganz ehrlich, diese Seite ist eine
     * Katastrophe und wirkt total unaufgeräumt und chaotisch."* Dazu: der
     * Fund „viel zu präsent", die Absicht „viel zu viel Information", und
     * *„wie wäre es überhaupt, wenn man die Zuhause-Seite mit Tabs erweitert,
     * sodass man mehr Übersicht bekommt"*. Den Entwurf dazu hat er mit
     * *„die Vorlage ist perfekt!!! Bitte exakt so bauen!!"* freigegeben.
     *
     * Gemessen war die Seite ZWEI Hüllen, und die rechte lief ab 1280 Punkten
     * als CSS-Mehrspaltensatz: Tugend und Frage des Tages standen je nach
     * Höhe der Nachbarn mal unten in der Mitte, mal oben rechts. Ein Raster,
     * das seine Blöcke selbst umsortiert, wirkt genau so, wie Martin es sagt.
     *
     * Jetzt drei Bereiche mit festem Raster:
     *   Heute      — was heute dran ist: Wege, der Impuls aus dem Spiegel,
     *                Haltung und Tat, echte Aufgaben, und „Nebenbei" auf
     *                zartem Grund (Absicht, Hinweis auf den Fund).
     *   Mein Tag   — Körper, Aufnahme, der Tag bisher, die letzte Aufnahme.
     *   Rückblick  — Ankermoment, Fund des Tages, gestern, Spiegel-Verlauf.
     *
     * ⚠️ Die Bausteine sind DIESELBEN wie vorher (blickTugend, blickFund …) —
     *    sie wandern, sie werden nicht neu erfunden. Alle Bereiche bleiben im
     *    Baum (nur `hidden`), damit das Füllen über `this.root` weiter trifft.
     */
    const haltungTat = document.createElement("div");
    haltungTat.className = "zuhause-haltung-tat";
    haltungTat.append(this.blickTugend(), this.blickHeute());
    const bereichHeute = this.baueBereich("heute");
    bereichHeute.append(
      this.blickTagesbild(),
      // Ticket 2561: die Morgenmeditation steht nicht mehr hier oben, sondern
      // als leises Angebot in „Nebenbei" (blickNebenbei).
      this.blickMorgenfrage(),
      this.witzKarte.wurzel,
      haltungTat,
      this.blickOffen(),
      this.blickNebenbei(),
    );
    const bereichTag = this.baueBereich("tag");
    bereichTag.append(
      this.blickKoerper(),
      this.blickAufnahme(),
      this.blickGestern(),
      this.blickZuletzt(),
    );
    const bereichRueckblick = this.baueBereich("rueckblick");
    bereichRueckblick.append(
      this.blickAnker(),
      this.blickFund(),
      this.blickVortag(),
      this.blickSpiegelVerlauf(),
    );
    // 13.10.2026 (Bahn zuhause-neu): der vierte Reiter „Neu“ — Karten und Merker in lib/neu-reiter.ts.
    const bereichNeu = this.baueBereich("neu");
    this.neuReiter = baueNeuReiter({
      uid: () => (window as unknown as { __voiceV8Uid?: number }).__voiceV8Uid,
      features: () => (window as unknown as { __voiceV8Features?: { ankermomente?: boolean } }).__voiceV8Features,
    });
    bereichNeu.append(this.neuReiter.wurzel);
    blick.append(bereichHeute, bereichTag, bereichRueckblick, bereichNeu);
    const reiter = this.baueReiter();

    /*
     * 2026-08-29: Der „Sprechen"-Knopf ist WEG.
     *
     *   Martin: „Im Zuhause brauchen wir natürlich den Sprechen-Button nicht
     *   mehr, weil der sowieso unten in der Dauerpräsenz zur Verfügung steht."
     *
     *   Dazu kam ein zweiter Grund, der beim Nachsehen auffiel: Der Knopf trug
     *   ein MIKROFONZEICHEN, tat aber `goToView("chat")` — er wechselte nur in
     *   den Chat und nahm nie etwas auf. Auge und Funktion sagten
     *   Verschiedenes; das war der Grund, warum er stoerte.
     *
     *   Das Ereignis `vespera:zuhause-sprechen` bleibt bestehen (app.ts:18042)
     *   — es hoert auch auf andere Ausloeser. Nur dieser Knopf faellt.
     */

    /*
     * ⚠️ 2026-09-03 — Der „Mehr"-Block ist WEG. Martin: *„Ich sehe da hin,
     * darin nicht wirklich einen Zweck. Eigentlich kann man das auch
     * entfernen. Siehst du einen Zweck dahinter, ganz ehrlich?"*
     *
     * Nein. Gemessen an seinem eigenen Verlauf:
     *
     *   Die fuenf Schnellfragen wurden in vier Monaten 13 MAL getippt (exakter
     *   Wortlaut, den nur der Knopf sendet: Wetter 3, Zug 5, Erinnerung 1,
     *   Gestern 3, Post 1) — bei 8774 Nachrichten sind das 0,15 %. Fast alle
     *   liegen zwischen dem 17. und 22. August dicht beieinander, das ist
     *   Ausprobieren, kein Benutzen. Letzter Tipp: 29. August.
     *
     *   Nach dem WETTER hat er dagegen 56 mal in eigenen Worten gefragt. Das
     *   Thema zaehlt, der Knopf nicht — und das Wetter steht als Kachel auf
     *   derselben Seite.
     *
     *   Der Merksatz („Sag's ihr. Sie merkt es. Sie erfindet nichts.") ist ein
     *   Satz fuer jemanden, der noch nicht weiss, was Vespera ist.
     *
     *   „Gesagt" zeigte seine letzten zwei Nachrichten als Weg in den Chat —
     *   seit es den Haus-Knopf zum Umschalten gibt, ein zweiter Weg an
     *   dieselbe Stelle, und die zwei Zeilen sind das Erste, was dort steht.
     *
     * ⚠️ Die fuenf Beispielfragen sind NICHT geloescht, sondern in das
     * Onboarding umgezogen (ZUHAUSE_WEGE, letzter Schritt „Was du sagen
     * kannst"). Sie waren die einzige Stelle der App, die zeigt, was man
     * ueberhaupt sagen kann — fuer Martin nutzlos, fuer einen neuen Nutzer
     * die einzige Anleitung.
     */

    /*
     * ⚠️ 2026-09-03 (Martin: "da stehen so viele Informationen, die ich
     * eigentlich nie brauche — ab wann genug Informationen da sind, um den
     * Praesenzspiegel zu machen, das kann man weggeben. Und unten steht
     * 'Zum Studio', braucht man auch nicht.")
     *
     * WEG: der Reife-Ring ("Dein Spiegel", Prozentzahl, Fortschrittssatz).
     * Er beantwortete eine Frage, die sich niemand stellt — und der Spiegel
     * selbst taucht ohnehin in der Offen-Liste auf, sobald er fertig ist
     * (siehe zeichenFuerOffen: kind === "spiegel"). Es geht also nichts
     * verloren ausser dem Warten-Balken.
     *
     * WEG: "Zum Studio". Ein Knopf, der dorthin fuehrt, wo man mit einem
     * Tipp auf die Fussleiste ohnehin hinkommt — und dieselbe Regel wie bei
     * der Fussleiste: nie ein Knopf, der dorthin fuehrt, wo man schon steht.
     */

    /*
     * Uhr und Wetter — 2026-08-29 nach OBEN geholt.
     *
     * ⚠️ Bis heute stand diese Zeile als LETZTES Element in `innen`, also im
     * eingeklappten „Mehr"-Block. Gemessen: `div.zuhause-mehr-innen` hat
     * `display: none`, die Zeile hatte damit Hoehe 0 — sie war fehlerfrei
     * gebaut, gefuellt und unsichtbar. Martin (29.08.): „wäre gut, wenn auch
     * dort das Wetter angezeigt wird vom heutigen Tag." Es WAR dort. Nur
     * hinter „und 9 weitere".
     *
     * Jetzt steht sie direkt unter der Begruessung, wo ein Morgenblick landet.
     */
    /*
     * Die gezeichnete Tagesszene (Stil „blatt") und die Wetter-Kachel
     * (Stil „kacheln") — 2026-08-29.
     *
     * Beide zeigen dasselbe Wetter, nur in zwei Gestalten. Sie stehen BEIDE
     * im Blatt und werden per CSS ueber `body[data-zuhause-stil]` ein- und
     * ausgeblendet: Nur eine ist je sichtbar, aber keine muss beim Umschalten
     * neu gebaut werden — der Wechsel ist dadurch augenblicklich.
     */
    const szene = document.createElement("div");
    szene.className = "zuhause-szene-halter";
    szene.dataset.slot = "szene";
    szene.setAttribute("aria-hidden", "true");
    /*
     * 2452: Die Szene stand leer, bis das Wetter da war. Karten und Gruss
     * waren schon sichtbar — dann rutschte das Bild nach, und alles darunter
     * sprang. Martin: das Bild soll da sein, bevor der Rest kommt.
     *
     * Deshalb sofort, aus Uhr und letztem Stand. Wetter darf spaeter
     * nachfaerben, nicht erst erscheinen.
     */
    const gemerkteSonne = this.liesSzeneSonne();
    // Nur wenn BEIDE Zeiten da sind, ist die Rechnung besser als der
    // Rueckfall auf feste Stunden.
    const voll =
      gemerkteSonne?.aufgang && gemerkteSonne.untergang
        ? { aufgang: gemerkteSonne.aufgang, untergang: gemerkteSonne.untergang }
        : null;
    this.fuelleSzene(
      this.liesSzeneLage(),
      tageszeitJetzt(new Date(), voll),
      szene,
      voll,
    );

    const wetterKachel = document.createElement("div");
    wetterKachel.className = "zuhause-wetterkachel";
    wetterKachel.dataset.slot = "wetterkachel";

    const meta = document.createElement("p");
    meta.className = "zuhause-meta";
    meta.dataset.slot = "meta";

    /*
     * Der Weg zurück in den Chat — schwebend, rechts unten (29.08.).
     *
     * Martin: *„Hier fehlt noch der Back Button zum Chat, ohne dass man den
     * Chat direkt startet. [...] Vielleicht auch dort, wo im Chat das
     * Häuschen ist, so vom Stil her, dass man hin und her switchen kann.
     * Mit dem rechten Daumen."*
     *
     * ## Warum es das brauchte
     *
     * Seit der Fußleisten-Umbau den Chat-Knopf durch „Menü" ersetzt hat, gab
     * es von Zuhause aus KEINEN direkten Weg in den Chat mehr. Der einzige
     * war „Zum Studio" — und der steckt im eingeklappten „Mehr"-Block, also
     * hinter zwei Tipps und außerhalb der Daumenzone.
     *
     * ⚠️ Er wechselt nur die Ansicht (`vespera:go-chat` → `goToView("chat")`)
     * und startet NICHTS: kein Mikrofon, kein neues Gespräch, kein Fokus ins
     * Textfeld. Genau das war Martins Bedingung. Das Mikrofon steht ohnehin
     * in der Fußleiste.
     *
     * ⚠️ Gleiche Stelle, gleiche Größe, gleicher leiser Ton wie das Haus im
     * Chat — die Werte stehen NICHT hier doppelt, sondern teilen sich in
     * style.css denselben Regelblock. Zwei Knöpfe, die ein Hin und Her
     * bilden, müssen ununterscheidbar sitzen; zwei getrennte Wertelisten
     * laufen auseinander.
     */
    const zumChat = document.createElement("button");
    zumChat.type = "button";
    zumChat.className = "zuhause-zum-chat";
    zumChat.setAttribute("aria-label", "Zum Chat");
    zumChat.title = "Chat";
    zumChat.innerHTML = zeichen("gespraech", 22);
    zumChat.addEventListener("click", () => {
      document.dispatchEvent(new CustomEvent("vespera:go-chat"));
    });

    /*
     * 2026-08-31 (Martin: "was ist jetzt mit dem Header" — der Entwurf von
     * heute frueh war genehmigt ("ja so passt es!"), ist aber nie gebaut
     * worden: gezeigt als Mockup, dann sprang das Gespraech zur naechsten
     * Frage, und die Umsetzung ging in der Zusammenfassung verloren).
     *
     * Genehmigt war: "ein Band ueber die ganze Flaeche, und der Gruss liegt
     * darin statt daneben. Ein weicher Verlauf traegt die Schrift, kein
     * Kasten." Dafuer muessen Szene, Gruss und Meta ein gemeinsamer
     * Rahmen sein — auf dem Telefon bleibt die Reihenfolge exakt wie
     * bisher (drei Kinder, 16px Abstand, ganz normal untereinander); die
     * Ueberlagerung ist eine reine Desktop-Regel (ab 768px) im Blatt.
     */
    const held = document.createElement("div");
    held.className = "zuhause-held";
    const lage = document.createElement("details");
    lage.className = "zuhause-lage";
    lage.dataset.slot = "lage";
    lage.setAttribute("aria-label", "Was gerade aktiv ist");

    held.append(szene, kopf, meta);
    /*
     * 05.09.2026 — die Lage-Zeile („Sonnet · CLI an · Extension wartet")
     * wandert ans Ende. Martin: *„Diese Information ist nur peripher
     * wichtig — nicht da, wo die Buttons sind, sondern am Ende von Zuhause."*
     * Sie stand direkt unter dem Gruß, an der zweitprominentesten Stelle
     * der Seite, für etwas, das man einmal am Tag wissen will.
     */
    /*
     * 06.09.2026 — Bärli wartet oberhalb des Seitenanfangs.
     *
     * Martin: „Ich möchte ein kleines verstecktes Bärli da aufsetzen. Wenn
     * ich die Seite runterziehe … so wie der kleine Claude-Roboter, der oben
     * an der Kante herumläuft."
     *
     * Er steht als erstes Kind im Blatt und wird per negativem Rand aus dem
     * Sichtfeld geschoben; sichtbar wird er nur beim Überziehen. Begründung
     * in lib/baerli.ts.
     */
    this.root.append(held, wetterKachel, reiter, blick, lage);
    this.zeichnePunkt();
    /*
     * 13.10.2026 (Martin: „komisch“): Projektzeile und Entwicklungs-Zahlen stehen NICHT mehr in Zuhause (die Bauteile leben anderswo weiter).
     *
     * ⚠️ Getrennt vom Zusammenbau oben, und zwar mit Absicht: Bärli ist
     *    Schmuck, kein Teil der Komposition. Vier Tests pinnen den Wortlaut
     *    genau dieser Append-Zeile („der Sprechen-Knopf ist weg", „die Lage
     *    steht am Ende", …) — was dort hineinwächst, bricht sie alle.
     */
    const baerli = document.createElement("div");
    baerli.innerHTML = baerliStreifen();
    const streifen = baerli.firstElementChild;
    if (streifen) this.root.prepend(streifen);
    /*
     * ⚠️ Der Knopf gehoert NICHT in `this.root`. Martin: *„Die Sprechblase
     * sollte nicht mitscrollen."*
     *
     * Gemessen: `section#view-zuhause` traegt selbst `overflow-y: auto` —
     * die Ansicht IST der Scroller. Ein Kind darin faehrt mit, auch ein
     * schwebendes: bei 400 Punkten Scroll wanderte der Knopf 228 Punkte
     * nach oben.
     *
     * Im Chat bleibt das Haeuschen stehen, und zwar aus einem Grund, der
     * nichts mit seiner Gestaltung zu tun hat: dort ist der Scroller
     * (`#chat`) ein GESCHWISTER des Knopfes, kein Vorfahre. Genau dieses
     * Verhaeltnis wird hier nachgebaut — der Knopf zieht eine Ebene hoeher,
     * neben den Scroller.
     *
     * `position: fixed` waere der andere Weg gewesen. Dagegen sprach, dass
     * der Knopf dann am Schirm klebt statt am Inhaltsbereich und die
     * Fusszeile mitsamt ihrem Sicherheitsabstand nicht mehr kennt — die
     * gemessenen 124 Punkte haetten neu geraten werden muessen.
     */
    this.zeigeGruss();
    this.zeigeMeta(formatUhr(), true);
    // Der leise Gruß (08.10.2026) — im Test aus: er hängt an der Uhrzeit.
    if (!IM_TEST) this.zeigeWillkommen();
    void this.fuelleLage();
    const ebene = this.root.parentElement ?? this.root;
    // Ein zweiter Aufbau darf keinen zweiten Knopf hinterlassen: `root`
    // wird geleert, diese Ebene nicht.
    ebene.querySelectorAll(".zuhause-zum-chat").forEach((e) => e.remove());
    ebene.append(zumChat);
  }

  /** Welcher Bereich gerade steht. Beim Öffnen immer „Heute". */
  private bereich: ZuhauseBereich = "heute";

  /** Ein leerer Bereich; `hidden`, solange sie nicht dran ist. */
  private baueBereich(name: ZuhauseBereich): HTMLElement {
    const t = document.createElement("div");
    t.className = `zuhause-bereich zuhause-bereich--${name}`;
    t.dataset.bereich = name;
    t.id = `zuhause-bereich-${name}`;
    t.setAttribute("role", "tabpanel");
    t.setAttribute("aria-labelledby", `zuhause-reiter-${name}`);
    t.hidden = name !== this.bereich;
    return t;
  }

  /**
   * Die Reiterleiste — dieselbe Norm wie Buch und Spiegel (`.book-tabs`),
   * kein eigenes Aussehen. Pfeiltasten wechseln wie in jeder Reiterleiste.
   */
  private baueReiter(): HTMLElement {
    const nav = document.createElement("nav");
    nav.className = "book-tabs zuhause-reiter";
    nav.setAttribute("role", "tablist");
    nav.setAttribute("aria-label", "Zuhause");
    const namen: Array<[ZuhauseBereich, string]> = [
      ["heute", "Heute"],
      ["tag", "Mein Tag"],
      ["rueckblick", "Rückblick"],
      ["neu", "Neu"],
    ];
    for (const [name, wort] of namen) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "book-tab zuhause-reiter-knopf";
      b.id = `zuhause-reiter-${name}`;
      b.dataset.bereich = name;
      b.setAttribute("role", "tab");
      b.setAttribute("aria-controls", `zuhause-bereich-${name}`);
      b.textContent = wort;
      if (name === "neu") b.append(this.baueNeuPunkt());
      b.addEventListener("click", () => this.zeigeBereich(name));
      b.addEventListener("keydown", (e) => {
        if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
        e.preventDefault();
        const i = namen.findIndex(([n]) => n === name);
        const schritt = e.key === "ArrowRight" ? 1 : namen.length - 1;
        const naechste = namen[(i + schritt) % namen.length]?.[0] ?? "heute";
        this.zeigeBereich(naechste);
        nav.querySelector<HTMLElement>(`[data-bereich="${naechste}"]`)?.focus();
      });
      nav.append(b);
    }
    this.markiereReiter(nav);
    return nav;
  }

  private markiereReiter(nav: ParentNode): void {
    nav.querySelectorAll<HTMLElement>(".zuhause-reiter-knopf").forEach((b) => {
      const an = b.dataset.bereich === this.bereich;
      b.classList.toggle("is-active", an);
      b.setAttribute("aria-selected", an ? "true" : "false");
      b.tabIndex = an ? 0 : -1;
    });
  }

  /** Auf einen Bereich wechseln — von der Leiste oder von einem Hinweis aus. */
  zeigeBereich(name: ZuhauseBereich): void {
    this.bereich = name;
    this.root.querySelectorAll<HTMLElement>(".zuhause-bereich").forEach((t) => {
      t.hidden = t.dataset.bereich !== name;
    });
    this.markiereReiter(this.root);
    if (name === "neu") this.neuReiter?.zeige();
    this.zeichnePunkt();
  }

  /** Der Reiter „Neu“ (Karten, Filter, Merker); gebaut in der Seite, gezeichnet beim Öffnen des Reiters. */
  private neuReiter: NeuReiter | null = null;
  /** Witz des Tages (16.10.2026) — lib/witz-karte.ts; lädt mit jedem refresh(). */
  private witzKarte: WitzKarte = baueWitzKarte();

  /** Der Punkt am Reiter „Neu“: da, solange es Ungesehenes gibt. Erst `zeichnePunkt` entscheidet, ob er zu sehen ist. */
  private baueNeuPunkt(): HTMLElement {
    const p = document.createElement("span");
    p.className = "zuhause-reiter-punkt";
    p.dataset.slot = "neu-punkt";
    p.setAttribute("aria-hidden", "true");
    p.hidden = true;
    return p;
  }

  private zeichnePunkt(): void {
    const knopf = this.root.querySelector<HTMLElement>("#zuhause-reiter-neu");
    if (!knopf) return;
    const an = this.bereich !== "neu" && !!this.neuReiter?.hatUngesehenes();
    const punkt = knopf.querySelector<HTMLElement>(".zuhause-reiter-punkt");
    if (punkt) punkt.hidden = !an;
    if (an) knopf.setAttribute("aria-label", "Neu, es gibt Ungesehenes");
    else knopf.removeAttribute("aria-label");
  }

  /**
   * „Nebenbei" — auf zartem Grund, ganz unten in Heute. (23.09.2026)
   *
   * Martin: *„Bitte unterscheide die wichtigen Sachen von den weniger
   * relevanten. Die kann man weiter unten und vielleicht sogar in einer
   * anderen, zarten Hintergrundfarbe begeben."* Hier stehen die Absicht (als
   * EINE Zeile) und der Hinweis auf den Fund, der im Rückblick wohnt.
   *
   * 24.09.2026 (Ticket 2561): davor, durch eine feine Linie abgesetzt, das
   * EINE Angebot des Tages (Meditation, Fund oder Dankbarkeit) — gezeichnet
   * von zeigeAngebot(), anfangs versteckt.
   *
   * 29.09.2026 (Ticket 2561): Die Tageszeit des Angebots („heute früh")
   * steht an der Überschrift, nicht als zweite Überschrift im Kasten — die
   * erste Zeile unter „Nebenbei" ist das Angebot selbst (siehe `kopf` in
   * lib/tagesangebot.ts). Auch dieses Stück zeichnet nur zeigeAngebot().
   */
  private blickNebenbei(): HTMLElement {
    const sec = document.createElement("aside");
    sec.className = "zuhause-nebenbei";
    sec.dataset.leiste = "nebenbei";
    const h = document.createElement("h2");
    h.className = "zuhause-blick-titel";
    h.textContent = "Nebenbei";
    const zeit = document.createElement("span");
    zeit.className = "zuhause-nebenbei-zeit";
    zeit.dataset.slot = "angebot-zeit";
    zeit.hidden = true;
    h.append(zeit);
    const flaeche = document.createElement("div");
    flaeche.className = "zuhause-nebenbei-flaeche";
    const angebot = document.createElement("div");
    angebot.className = "zuhause-angebot";
    angebot.dataset.slot = "angebot";
    angebot.hidden = true;
    const fund = document.createElement("button");
    fund.type = "button";
    fund.className = "zuhause-fund-hinweis";
    fund.dataset.slot = "fund-hinweis";
    fund.hidden = true;
    fund.addEventListener("click", () => this.zeigeBereich("rueckblick"));
    flaeche.append(angebot, this.blickAbsicht(), fund);
    sec.append(h, flaeche);
    return sec;
  }

  /**
   * „Gestern" im Rückblick — die Zusammenfassung des Vortags, zugeklappt.
   * Gefüllt in refresh() aus derselben Alltag-Ladung wie das Tagesblatt.
   */
  private blickVortag(): HTMLElement {
    const sec = document.createElement("section");
    sec.className = "zuhause-vortag";
    sec.dataset.leiste = "vortag";
    sec.hidden = true;
    const h = document.createElement("h2");
    h.className = "zuhause-blick-titel";
    h.textContent = "Gestern";
    const lade = document.createElement("details");
    lade.className = "zuhause-vortag-lade";
    const kopf = document.createElement("summary");
    kopf.className = "zuhause-vortag-kopf";
    const text = document.createElement("div");
    text.className = "zuhause-vortag-text";
    lade.append(kopf, text);
    sec.append(h, lade);
    return sec;
  }

  private zeigeVortag(tagSchluessel: string | null, text: string | null): void {
    const sec = this.root.querySelector<HTMLElement>('[data-leiste="vortag"]');
    const kopf = sec?.querySelector<HTMLElement>(".zuhause-vortag-kopf");
    const feld = sec?.querySelector<HTMLElement>(".zuhause-vortag-text");
    if (!sec || !kopf || !feld) return;
    const ohneKopf = (text ?? "").replace(/^\s*#\s[^\n]*\n?/, "").trim();
    if (!tagSchluessel || !ohneKopf) {
      sec.hidden = true;
      return;
    }
    const d = new Date(`${tagSchluessel}T12:00:00`);
    kopf.textContent = Number.isNaN(d.getTime())
      ? tagSchluessel
      : d.toLocaleDateString("de-AT", {
          weekday: "long",
          day: "numeric",
          month: "long",
        });
    renderMarkdown(feld, ohneKopf);
    sec.hidden = false;
  }

  /** Der Weg zu allen Spiegeln — im Rückblick, wo das Gewesene steht. */
  private blickSpiegelVerlauf(): HTMLElement {
    const sec = document.createElement("section");
    sec.className = "zuhause-spiegel-verlauf";
    sec.dataset.leiste = "spiegel-verlauf";
    const h = document.createElement("h2");
    h.className = "zuhause-blick-titel";
    h.textContent = "Spiegel";
    const b = document.createElement("button");
    b.type = "button";
    b.className = "zuhause-weiter-karte";
    const wort = document.createElement("span");
    wort.textContent = "Alle Spiegel im Verlauf";
    const pfeil = document.createElement("span");
    pfeil.className = "zuhause-weiter-pfeil";
    pfeil.setAttribute("aria-hidden", "true");
    pfeil.textContent = "›";
    b.append(wort, pfeil);
    b.addEventListener("click", () => {
      document.dispatchEvent(new CustomEvent("vespera:spiegel-verlauf"));
    });
    sec.append(h, b);
    return sec;
  }

  /** Überschrift des Tagesblatts — siehe tagesblattTitel(). */
  private zeigeBlattTitel(titel: string): void {
    // 06.09.: Der Titel gehört zur Zusammenfassung (Gestern/Heute), nicht
    // mehr zum Impuls-Abschnitt — der heißt immer „Heute".
    // 23.09.: In „Mein Tag" heißt der heutige Stand „Dein Tag bisher" —
    // ein zweites „Heute" neben dem Reiter „Heute" wäre ein Rätsel.
    if (titel === "Heute") titel = "Dein Tag bisher";
    const h = this.root.querySelector<HTMLElement>(
      '[data-leiste="gestern"] .zuhause-blick-titel',
    );
    if (h && h.textContent !== titel) h.textContent = titel;
  }

  /**
   * Die Tageszusammenfassung — „Gestern" bis der heutige Tag verdichtet ist,
   * dann „Heute" (Titel über zeigeBlattTitel). Gerendertes Markdown statt
   * einer Zeile Rohtext; die erste Überschrift („# Tageszusammenfassung
   * 2026-09-05") entfällt, der Abschnittstitel sagt es schon.
   */
  private blickGestern(): HTMLElement {
    const sec = document.createElement("section");
    sec.className = "zuhause-gestern";
    sec.dataset.leiste = "gestern";
    sec.hidden = true;
    const kopf = document.createElement("div");
    kopf.className = "zuhause-blick-kopf";
    const h = document.createElement("h2");
    h.className = "zuhause-blick-titel";
    h.textContent = "Gestern";
    // Der Platz für den Vorlese-Knopf — gefüllt von app.ts, sobald Text da
    // ist (voice-v8:vorlese-knopf). Leer nimmt er keinen Raum.
    const vorlesen = document.createElement("span");
    vorlesen.className = "zuhause-vorlesen";
    kopf.append(h, vorlesen);
    /*
     * ⚠️ 10.09.2026 — GESTERN IST ZU, BIS MAN LESEN WILL.
     *
     * Martin: *„Die Information gestern ist auch extrem lang. Die kann man
     * auch zum Teil einklappen, weil man sie sowieso nur hört."*
     *
     * Im Browser gemessen: der Block war **1.788 px** hoch — mehr als zwei
     * Bildschirme von rund 4.100 auf der ganzen Seite. Zugeklappt bleiben
     * zwei Zeilen als Anlesetext stehen; wer hoeren will, hat den
     * Vorlese-Knopf daneben, und der bleibt IMMER sichtbar.
     *
     * ⚠️ Bewusst kein `details` um den ganzen Abschnitt: Der Vorlese-Knopf
     *    sitzt in der Kopfzeile, und in einem `summary` wuerde jeder Klick
     *    darauf zusaetzlich auf- und zuklappen.
     */
    const blatt = document.createElement("div");
    blatt.className = "zuhause-tagesblatt";
    blatt.dataset.zu = "1";
    /*
     * ⚠️ 10.09.2026, ZWEITER ANLAUF — DER PFEIL AUS DEM CHAT, NICHT EIN
     *    EIGENES MUSTER.
     *
     * Im ersten Anlauf stand oben in der Kopfzeile eine Pille „Ganzen Text",
     * und der Text war hart auf vier Zeilen geschnitten. Martin: *„Das, was
     * gestern steht, schaut total komisch aus, weil es unten abgeschnitten
     * wirkt. Mach einfach einen kleinen Pfeil, so wie wir es schon im Chat
     * haben, wo der Text dann ausblendet … Ich wollte alles einheitlich
     * ausschauen und nicht einmal da so."*
     *
     * Der Chat kann das seit dem 29.07.: `.bubble.is-collapsed` deckelt die
     * Hoehe und laesst die letzten Zeilen ueber eine Maske ausblenden. Genau
     * das gilt jetzt hier — kein zweites Faltungs-Muster in derselben App.
     */
    const pfeil = document.createElement("button");
    pfeil.type = "button";
    pfeil.className = "zuhause-gestern-pfeil";
    pfeil.setAttribute("aria-label", "Ganzen Text zeigen");
    pfeil.setAttribute("aria-expanded", "false");
    pfeil.addEventListener("click", () => {
      const zu = blatt.dataset.zu === "1";
      if (zu) delete blatt.dataset.zu;
      else blatt.dataset.zu = "1";
      pfeil.setAttribute("aria-expanded", zu ? "true" : "false");
      pfeil.setAttribute(
        "aria-label",
        zu ? "Text wieder einklappen" : "Ganzen Text zeigen",
      );
    });
    sec.append(kopf, blatt, pfeil);
    return sec;
  }
  private zeigeTagesblatt(text: string): void {
    const sec = this.root.querySelector<HTMLElement>('[data-leiste="gestern"]');
    const blatt = sec?.querySelector<HTMLElement>(".zuhause-tagesblatt");
    if (!sec || !blatt) return;
    const ohneKopf = text.replace(/^\s*#\s[^\n]*\n?/, "").trim();
    if (!ohneKopf) {
      sec.hidden = true;
      return;
    }
    renderMarkdown(blatt, ohneKopf);
    sec.hidden = false;
    /*
     * 06.09.: Vorlesen (Martin: „sehr viel Text"). Gelesen wird der
     * gerenderte Text, nicht das Markdown — sonst hörte man Rauten und
     * Sternchen. Der Knopf kommt aus app.ts (derselbe wie an den Blasen).
     */
    const platz = sec.querySelector<HTMLElement>(".zuhause-vorlesen");
    if (platz) {
      window.dispatchEvent(
        new CustomEvent("voice-v8:vorlese-knopf", {
          detail: { text: blatt.innerText, platz },
        }),
      );
    }
  }

  /**
   * Die Körper-Karte — Whoop im Zuhause. (06.09.2026, Martin: „die
   * wichtigsten Daten sehen und draufklicken können … wenn Whoop noch nicht
   * aktualisiert ist, darauf hinweisen und ausgrauen.")
   *
   * Frisch heißt: die Erholung trägt das heutige Datum. Sonst steht der
   * letzte Stand dabei, und die Karte ist gedimmt — der Smiley darf nicht
   * so tun, als wüsste er, wie es dir jetzt geht.
   */
  /**
   * Die Aufnahme, mit zwei Schaltern. (06.09.2026)
   *
   * Martin: *„In diesem Zusammenhang wäre super, wenn im Zuhause schnell
   * Buttons wären, die zum Beispiel das Ein- und Ausschalten Daueraufnahme
   * oder manuelle Aufnahme."*
   *
   * Zwei Dinge, die man auseinanderhalten muss:
   *
   *   - **Durchgehend** ist eine ABSICHT. Sie überlebt Pausen, Neustarts und
   *     abgeräumte Sitzungen; die Wache in {@link ./aufnahme-wache.js} setzt
   *     sie durch.
   *   - **Jetzt aufnehmen** ist eine einzelne Sitzung, von Hand begonnen und
   *     beendet.
   *
   * Der Zustand steht im Klartext daneben, denn genau daran hat es gehakt:
   * „pausiert" war minutenlang wahr, ohne dass es jemand sah.
   */
  private blickAufnahme(): HTMLElement {
    const sec = document.createElement("section");
    sec.className = "zuhause-aufnahme";
    sec.dataset.leiste = "aufnahme";
    sec.hidden = true;
    const h = document.createElement("h2");
    h.className = "zuhause-blick-titel";
    h.textContent = "Aufnahme";
    const karte = document.createElement("div");
    karte.className = "zuhause-aufnahme-karte";

    const zeile = document.createElement("div");
    zeile.className = "zuhause-aufnahme-zeile";
    const punkt = document.createElement("span");
    punkt.className = "zuhause-aufnahme-punkt";
    punkt.setAttribute("aria-hidden", "true");
    const stand = document.createElement("p");
    stand.className = "zuhause-aufnahme-stand";
    stand.setAttribute("role", "status");
    stand.setAttribute("aria-live", "polite");
    zeile.append(punkt, stand);

    /*
     * 07.09.2026 — die drei Knoepfe kommen jetzt aus der Pult-Bibliothek.
     *
     * Martin, mit Bild: *„Das sieht nicht aus wie ein Steuerpult, sondern
     * einfach nur wie drei Felder ... kann man bitte die ganzen Player- und
     * Abspiel- und Aktivierungs-Buttons vereinheitlichen, dass es aus einer
     * Bibliothek kommt?"* Begruendung und Zeichen: lib/steuerpult.ts.
     */
    /*
     * ⚠️ Schon beim Bauen mit Zeichen. `zeichne()` beschriftet gleich um —
     *    aber bis der echte Zustand aus der nativen Huelle da ist, vergeht
     *    ein Moment, und in dem stand hier sonst ein Pult ohne Zeichen.
     */
    const knoepfe = bauePult([
      {
        rolle: "dauer",
        name: "Durchgehend",
        zeichen: PULT_ZEICHEN.kippAus,
      },
      {
        rolle: "pause",
        name: "Pause",
        zeichen: PULT_ZEICHEN.anhalten,
        gesperrt: true,
      },
      {
        rolle: "jetzt",
        name: "Aufnehmen",
        zeichen: PULT_ZEICHEN.aufnehmen,
      },
    ]);
    const holeSchalter = (rolle: string): HTMLButtonElement =>
      knoepfe.querySelector<HTMLButtonElement>(
        `.pult-schalter[data-rolle="${rolle}"]`,
      )!;
    const dauer = holeSchalter("dauer");
    const pause = holeSchalter("pause");
    const jetzt = holeSchalter("jetzt");

    karte.append(zeile, knoepfe);
    sec.append(h, karte);
    void this.verdrahteAufnahme(sec, punkt, stand, knoepfe, dauer, jetzt, pause);
    return sec;
  }

  private async verdrahteAufnahme(
    sec: HTMLElement,
    punkt: HTMLElement,
    stand: HTMLElement,
    /** Das Pult selbst — `setzePult` beschriftet darin um. */
    knoepfe: HTMLElement,
    dauer: HTMLButtonElement,
    jetzt: HTMLButtonElement,
    pause: HTMLButtonElement,
  ): Promise<void> {
    try {
      const rec = await import("./native-recorder.js");
      const { loadSettings } = await import("./settings.js");
      /*
       * ⚠️ 22.09.2026 — hier stand isRecorderAvailable() mit der
       *    Begruendung „ohne native Huelle gibt es keine Tagesaufnahme".
       *    Die gilt seit dem 17.09. nicht mehr; im Browser laeuft sie.
       *    Weg bleibt der Block nur ohne Mikrofon.
       */
      if (!rec.kannAufzeichnen()) return;
      sec.hidden = false;

      /*
       * 23.09.2026 — Martin: „Das Live-Recording aktiv schalten funktioniert
       * nicht." Scheiterte der Start, stand danach wieder „aus" da (oder das
       * rohe Wort „error") — als hätte niemand gedrückt. Der Grund steht jetzt
       * auf der Karte, bis eine Aufnahme wirklich läuft.
       */
      let startFehler: string | null = null;
      const merkeFehler = (e: Error): void => {
        startFehler = e.message || "unbekannter Grund";
      };
      const zeichne = (): void => {
        const z = rec.getRecorderState();
        if (z === "recording") startFehler = null;
        const dauerAn = loadSettings().alwaysRecord === true;
        sec.dataset.zustand = z;
        const laeuft = z === "recording" || z === "paused";
        /*
         * Ein Wort je Schalter, der Zustand steckt im Zeichen. „Durchgehend
         * aus" stand auf zwei Zeilen und las sich wie Fliesstext.
         *
         * ⚠️ Pause wird GESPERRT, nicht versteckt: sonst wandern die anderen
         *    beiden Schalter bei jedem Zustandswechsel unter dem Finger weg.
         */
        setzePult(knoepfe, [
          {
            rolle: "dauer",
            name: "Durchgehend",
            zeichen: dauerAn ? PULT_ZEICHEN.kippAn : PULT_ZEICHEN.kippAus,
            an: dauerAn,
            ansage: `Durchgehend aufnehmen: ${dauerAn ? "an" : "aus"}`,
          },
          {
            rolle: "pause",
            name: z === "paused" ? "Weiter" : "Pause",
            zeichen:
              z === "paused" ? PULT_ZEICHEN.abspielen : PULT_ZEICHEN.anhalten,
            gesperrt: !laeuft,
          },
          {
            rolle: "jetzt",
            name: laeuft ? "Beenden" : "Aufnehmen",
            zeichen: laeuft ? PULT_ZEICHEN.beenden : PULT_ZEICHEN.aufnehmen,
          },
        ]);
        const gruende = rec.offenePausenGruende();
        const worte: Record<string, string> = {
          recording: laufText("läuft"),
          // Steht ein Grund fest, gehört er dazu — „pausiert" allein lässt
          // einen rätseln, und genau das war Martins Ärger.
          paused: pauseText(
            gruende,
            gruende.length > 0 ? `pausiert (${gruende.join(", ")})` : "pausiert",
          ),
          finishing: "wird abgeschlossen",
          idle: dauerAn ? "wartet auf die nächste Sitzung" : "aus",
          error: "gestört",
        };
        const anz = pauseAnzeige(gruende);
        stand.classList.toggle("is-wartet", z === "paused" && (anz === "wartet" || anz === "unbekannt"));
        stand.textContent =
          startFehler && (z === "idle" || z === "error")
            ? `Start gescheitert: ${startFehler}`
            : (worte[z] ?? z);
      };

      zeichne();
      rec.observeRecorderGlobal(() => zeichne());
      window.addEventListener("voice-v8:recorder-state", () => zeichne());
      // Ticket 2849: der Zuruf kann die Absicht ändern, ohne dass der Rekorder sich bewegt (im Anruf).
      window.addEventListener("voice-v8:alltag-absicht", () => zeichne());

      dauer.addEventListener("click", () => {
        void (async () => {
          dauer.disabled = true;
          try {
            const an = loadSettings().alwaysRecord !== true;
            // Ticket 2849: dieselben Schritte nimmt der Zuruf „Alltag starten/beenden“ —
            // EINE Stelle (alltag-steuern.ts), kein zweiter Besitzer.
            await schalteDurchgehend(an, await echterAnschluss(), merkeFehler);
          } finally {
            dauer.disabled = false;
            zeichne();
          }
        })();
      });

      pause.addEventListener("click", () => {
        // Ticket 2708 (Nachbesserung): Der Knopf tut, was er ZEIGTE — „Weiter"
        // setzt fort, „Pause" pausiert. Ein Umschalter nach dem echten
        // Zustand pausierte auf ein veraltetes „Weiter" hin.
        const weiter = sec.dataset.zustand === "paused";
        void (async () => {
          pause.disabled = true;
          try {
            // Das Fortsetzen räumt fremde Pausengründe, sonst hält ein
            // vergessener Eintrag die Aufnahme für immer an.
            await (weiter ? rec.resumeRecorderForUser() : rec.pauseRecorderForUser());
            await rec.syncFromNative().catch(() => undefined);
          } finally {
            pause.disabled = false;
            zeichne();
          }
        })();
      });

      jetzt.addEventListener("click", () => {
        void (async () => {
          jetzt.disabled = true;
          try {
            const z = rec.getRecorderState();
            if (z === "recording" || z === "paused") {
              await rec.stopRecordingForUser();
            } else {
              // startRecording nimmt das Aus selbst zurück — eine Stelle.
              startFehler = null;
              await rec.startRecording({ onError: merkeFehler });
            }
            await rec.syncFromNative().catch(() => undefined);
          } finally {
            jetzt.disabled = false;
            zeichne();
          }
        })();
      });
    } catch {
      /* Eine Übersicht darf nie an einem Knopf scheitern. */
    }
  }

  private blickKoerper(): HTMLElement {
    const sec = document.createElement("section");
    sec.className = "zuhause-koerper";
    sec.dataset.leiste = "koerper";
    sec.hidden = true;
    const h = document.createElement("h2");
    h.className = "zuhause-blick-titel";
    h.textContent = "Körper";
    const karte = document.createElement("div");
    karte.className = "zuhause-koerper-karte";
    sec.append(h, karte);
    this.koerperSec = sec;
    this.koerperKarte = karte;
    void this.fuelleKoerper(sec, karte);
    return sec;
  }

  private async fuelleKoerper(sec: HTMLElement, karte: HTMLElement): Promise<void> {
    try {
      const r = await fetch("/api/whoop/summary?days=1", { credentials: "same-origin" });
      if (!r.ok) return;
      const d = (await r.json()) as {
        ok?: boolean;
        connected?: boolean;
        tage?: Array<{
          datum?: string | null;
          erholung_pct?: number | null;
          hrv_ms?: number | null;
          ruhepuls?: number | null;
          tagesbelastung?: number | null;
          schlaf?: { dauer_h?: number | null; defizit_h?: number | null } | null;
        }>;
      };
      const t = d?.tage?.[0];
      if (!d?.ok || d.connected === false || !t) return;
      /*
       * ⚠️ 06.09.2026, 08:35 — Martin: „Warum steht hier 44 Prozent? Ich seh
       * 64 Prozent." Die alte Kopfzeilen-Glut hatte Geist und Seele aus der
       * Stimmungs-Reihe mit der Erholung verrechnet (das Minimum). Unter
       * dem Wort „Erholung" darf aber nur Whoops Zahl stehen — alles andere
       * ist eine Lüge mit Prozentzeichen. Die Karte heißt „Körper".
       */
      const score = t.erholung_pct ?? null;
      const frisch = (t.datum ?? "") === wienTagSchluessel();
      const stufe = stufeFuer(score);
      karte.dataset.stufe = stufe;
      karte.dataset.stand = frisch ? "frisch" : "veraltet";
      karte.replaceChildren();

      /*
       * Martin: „wenn ich draufklicke, sollte ich auf die Detailansicht von
       * unserem Whoop kommen und nicht auf eine Webseite von Whoop." Die
       * ganze Karte ist EIN Knopf und öffnet die Verfassung (14 Tage, Ring,
       * Schlaf) — kein Link nach draußen.
       */
      const kopf = document.createElement("button");
      kopf.type = "button";
      kopf.className = "zuhause-koerper-kopf";
      kopf.setAttribute("aria-label", "Verfassung öffnen — Whoop im Detail");
      kopf.addEventListener("click", () => {
        location.hash = "#verfassung";
      });
      /*
       * ⚠️ 10.09.2026 — DREI GESICHTER STATT EINEM.
       *
       * Martin: *„das Gesicht vielleicht noch zu zwei weiteren Gesichtern
       * dazu machen, sodass der Erholungswert sich noch besser
       * widerspiegelt. Dann nur die Anzahl der Prozente angeben."*
       *
       * Ein einzelnes Gesicht sagt „so geht es dir". Drei sagen „so geht es
       * dir AUF DIESER SKALA" — man sieht, wo man steht, ohne die Zahl zu
       * deuten. Das aktive ist voll, die anderen treten zurueck.
       *
       * ⚠️ Das Wort „Erholung" faellt weg (Martins Wunsch), aber NICHT aus
       *    dem barrierefreien Namen: Eine nackte Zahl ohne Bedeutung ist
       *    schlimmer als keine — dieselbe Regel wie bei der Zug-Dauer.
       */
      const skala = document.createElement("span");
      skala.className = "zuhause-koerper-skala";
      for (const s of ["niedrig", "mittel", "gut"] as const) {
        const g = document.createElement("span");
        g.className = "zuhause-gesicht";
        g.dataset.stufe = s;
        if (s === stufe) g.dataset.aktiv = "1";
        g.innerHTML = smileySvg(s);
        skala.append(g);
      }
      const haupt = document.createElement("span");
      haupt.className = "zuhause-koerper-haupt";
      haupt.textContent = score != null ? `${Math.round(score)} %` : "—";
      kopf.setAttribute(
        "aria-label",
        score != null
          ? `Erholung ${Math.round(score)} Prozent — Verfassung öffnen`
          : "Erholung unbekannt — Verfassung öffnen",
      );
      const zeile = document.createElement("span");
      zeile.className = "zuhause-koerper-zeile";
      zeile.textContent = frisch ? verfassungsZeile(stufe, t.schlaf?.defizit_h) : "Whoop ist noch nicht aktualisiert.";
      const oben = document.createElement("span");
      oben.className = "zuhause-koerper-oben";
      oben.append(skala, haupt, zeile);
      kopf.append(oben);

      const werte = document.createElement("span");
      werte.className = "zuhause-koerper-werte";
      const wert = (name: string, v: string | null): void => {
        if (v == null) return;
        const el = document.createElement("span");
        el.className = "zuhause-koerper-wert";
        const z = document.createElement("b");
        z.textContent = v;
        const n = document.createElement("small");
        n.textContent = name;
        el.append(z, n);
        werte.append(el);
      };
      const komma = (x: number | null | undefined, stellen = 1): string | null =>
        typeof x === "number" ? x.toFixed(stellen).replace(".", ",") : null;
      wert("Schlaf", t.schlaf?.dauer_h != null ? `${komma(t.schlaf.dauer_h)} h` : null);
      wert("Belastung", komma(t.tagesbelastung));
      wert("HRV", t.hrv_ms != null ? `${Math.round(t.hrv_ms)} ms` : null);
      wert("Puls", t.ruhepuls != null ? `${Math.round(t.ruhepuls)}` : null);

      const stand = document.createElement("span");
      stand.className = "zuhause-koerper-stand";
      stand.textContent = frisch
        ? "Stand heute"
        : `Letzter Stand ${t.datum ? kleinAnfang(tagesblattTitel(t.datum, wienTagSchluessel())) : "unbekannt"}`;
      werte.append(stand);
      /*
       * ⚠️ 10.09.2026 — DIE WERTE SIND ZU, BIS MAN SIE SEHEN WILL.
       *
       * Martin: *„Die Parameter kann man einklappen. Die möchte ich nur
       * aufklappen, wenn ich sie sehen will. Das heißt, wir sparen uns eine
       * Menge Platz."*
       *
       * ⚠️ Das `details` steht NEBEN dem Knopf, nicht darin: Ein
       *    bedienbares Element in einem Knopf ist ungueltiges HTML, und der
       *    Klick ginge an beide.
       */
      const mehr = document.createElement("details");
      mehr.className = "zuhause-koerper-mehr";
      const zusammen = document.createElement("summary");
      zusammen.className = "zuhause-koerper-mehr-kopf";
      zusammen.textContent = "Werte";
      mehr.append(zusammen, werte);
      karte.append(kopf, mehr);
      sec.hidden = false;
    } catch {
      /* ohne Whoop bleibt die Karte verborgen */
    }
  }

  private blickHeute(): HTMLElement {
    const sec = document.createElement("section");
    sec.className = "zuhause-heute zuhause-karte";
    sec.dataset.leiste = "heute";
    const h = document.createElement("h2");
    h.className = "zuhause-blick-titel";
    // 06.09.: „Impuls" statt „Heute" — Martin: „was bedeutet bei Dein Tag
    // Heute?" 23.09.: „Tat des Tages" (Entwurf, freigegeben) — neben der
    // Haltung ist es die Tat, und „Impuls" heißt jetzt der Text aus dem
    // Spiegel darüber. Zweimal dasselbe Wort auf einer Seite wäre das
    // Durcheinander, das die „Frage" schon dreimal hatte.
    h.append("Tat");
    const breit = document.createElement("span");
    breit.className = "zuhause-nur-breit";
    breit.textContent = " des Tages";
    h.append(breit);
    const liste = document.createElement("div");
    liste.className = "zuhause-liste";
    liste.append(this.geruestZeile(true));
    sec.append(h, liste);
    return sec;
  }

  private blickOffen(): HTMLElement {
    const sec = document.createElement("section");
    sec.className = "zuhause-offen";
    sec.dataset.leiste = "tun";
    const h = document.createElement("h2");
    h.className = "zuhause-blick-titel";
    h.textContent = "Offen";

    const liste = document.createElement("div");
    liste.className = "zuhause-liste";
    liste.append(this.geruestZeile(false), this.geruestZeile(false));
    sec.append(h, liste);
    return sec;
  }

  /**
   * "Zuletzt" — die letzte Aufnahme, direkt von zuhause abspielbar.
   *
   * Aus dem Entwurf vom 25.08.: der Rueckblick oben sagt, WAS gestern war;
   * hier steht, was zuletzt AUFGENOMMEN wurde — mit einem Weg hinein.
   * Bleibt versteckt, solange es nichts gibt.
   */
  private blickZuletzt(): HTMLElement {
    const sec = document.createElement("section");
    sec.className = "zuhause-zuletzt";
    sec.dataset.leiste = "zuletzt";
    sec.hidden = true;
    const h = document.createElement("h2");
    h.className = "zuhause-blick-titel";
    h.textContent = "Zuletzt";
    const liste = document.createElement("div");
    liste.className = "zuhause-liste";
    sec.append(h, liste);
    return sec;
  }

  /**
   * Ankermoment des Tages — die erste Stelle im Zuhause, die ein BILD zeigt.
   *
   * Martin am 03.09.2026: *„wurde das Ankermoment auch mit dem Zuhause in
   * schöner Verbindung gebracht, dass dort auch ein Bild erscheinen kann oder
   * eins, das man aufklappen kann, damit das Ganze noch lebendiger wird?"*
   *
   * Bis dahin tauchte ein Anker NUR auf, wenn man danach fragte: vier Bilder
   * mit Frieden, Dankbarkeit, Befreiung und Verbundenheit lagen als Archiv da.
   *
   * ⚠️ Der Abschnitt bleibt VERSTECKT, bis wirklich ein Anker da ist. Eine
   * leere Überschrift „Ankermoment" wäre eine Aufforderung, keine Übersicht —
   * und wer noch keinen festgehalten hat, soll keine Lücke sehen.
   *
   * ⚠️ Das Bild wird NACHgeladen. Die Übersicht darf nie auf ein Foto warten;
   * fällt der Endpunkt aus, bleibt der Abschnitt einfach weg.
   */
  private blickAnker(): HTMLElement {
    const sec = document.createElement("section");
    sec.className = "zuhause-anker";
    sec.dataset.leiste = "anker";
    sec.hidden = true;
    const h = document.createElement("h2");
    h.className = "zuhause-blick-titel";
    h.textContent = "Ankermoment";
    const liste = document.createElement("div");
    liste.className = "zuhause-liste";
    sec.append(h, liste);

    void fetch("/api/anker/heute", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: AnkerAntwort | null) => {
        const a = d?.anker;
        if (!a?.bildUrl) return;
        liste.append(this.baueAnkerZeile(a, d?.gesamt ?? 1));
        sec.hidden = false;
      })
      .catch(() => {
        /* still — lieber kein Abschnitt als eine kaputte Übersicht */
      });
    return sec;
  }

  /** Eine Zeile mit Vorschaubild; Antippen klappt das ganze Bild auf. */
  private baueAnkerZeile(a: AnkerMoment, gesamt: number): HTMLElement {
    const lade = document.createElement("details");
    lade.className = "zuhause-anker-lade";

    const kopf = document.createElement("summary");
    kopf.className = "zuhause-zeile zuhause-zeile--anker";
    const bild = document.createElement("img");
    bild.className = "zuhause-anker-vorschau";
    bild.src = a.bildUrl;
    bild.alt = "";
    bild.loading = "lazy";
    // Ein kaputtes Bild darf die Zeile nicht sprengen — dann eben ohne.
    bild.addEventListener("error", () => bild.remove());
    const text = document.createElement("span");
    text.className = "zuhause-zeile-text";
    const titel = document.createElement("span");
    titel.className = "zuhause-zeile-titel";
    titel.textContent = a.gefuehl;
    text.append(titel);
    const neben = document.createElement("span");
    neben.className = "zuhause-zeile-datum";
    neben.textContent = a.ort
      ? a.ort
      : gesamt > 1
        ? `einer von ${gesamt}`
        : "festgehalten";
    text.append(neben);
    kopf.append(bild, text);
    lade.append(kopf);

    const gross = document.createElement("img");
    gross.className = "zuhause-anker-gross";
    gross.alt = a.gefuehl;
    gross.loading = "lazy";
    gross.src = a.bildUrl;
    gross.addEventListener("error", () => gross.remove());
    lade.append(gross);

    if (a.situation) {
      const p = document.createElement("p");
      p.className = "zuhause-anker-situation";
      p.textContent = a.situation;
      lade.append(p);
    }
    return lade;
  }

  /**
   * "Tugend" — eine pro Tag, aus Martins eigener 101er-Liste.
   *
   * Martin (30.08.2026): *„wir hatten ja mal eine Seite, die nannte sich der
   * Tugend Coach … hundertein Tugenden … Was hältst du davon?"*
   *
   * ⚠️ Bewusst der LEISESTE Abschnitt und der letzte. Zuhause ist sein
   * Morgenblick; was dort steht, muss verdienen, gesehen zu werden. Heute,
   * Offen und Zuletzt sagen ihm, was ansteht — die Tugend ist ein Angebot,
   * kein Anspruch. Deshalb eine Zeile, kein Feld.
   *
   * Kein Ladevorgang, kein Speicher: die Tugend rechnet sich aus dem Datum
   * (siehe shared/tugenden.ts). Sie steht also sofort da, auch offline, und
   * auf jedem Geraet dieselbe.
   */
  /**
   * „Dein Tag" — was heute geschehen ist. (05.09.2026)
   *
   * Martin wollte einen Erfolgsbalken; auf Nachfrage: *„soll kein Druck und
   * kein Wettkampf werden, sondern soll mich einfach sanft begleiten und
   * einladen."*
   *
   * ⚠️ Deshalb: kein Balken, keine Zahl, kein Rot. Vier Felder, die
   *    aufleuchten, wenn etwas geschehen ist, und sonst still bleiben. Ein
   *    Feld, das eine Lücke ANZEIGT, wäre am Abend ein Vorwurf — und ein
   *    System für Selbstreflexion darf nicht anklagen, wenn der Tag schwer
   *    war. Die vollständige Begründung steht in `server/tagesbild.ts`.
   */
  private blickTagesbild(): HTMLElement {
    const sec = document.createElement("section");
    sec.className = "zuhause-tagesbild";
    sec.dataset.leiste = "tagesbild";
    // Bis die Zahlen da sind: nicht da. Ein leeres Gerüst behauptet, es
    // sei nichts geschehen — und das weiss es noch gar nicht.
    sec.hidden = true;
    const h = document.createElement("h2");
    h.className = "zuhause-blick-titel";
    /*
     * ⚠️ 16.09.2026 — HIESS "Dein Tag". Martin: *„Klingen vielleicht etwas zu
     *    stressig. Da sollte man sich noch mal das Konzept etwas ueberarbeiten,
     *    damit man nicht glaubt, dass das etwas zu tun ist, sondern alles
     *    freiwillig und entspannt sein soll. Sonst hat man den Druck, es
     *    vollstaendig machen zu muessen."*
     *
     *    „Dein Tag" liest sich wie ein Tagesplan — etwas, das man abarbeitet.
     *    „Wege" sagt dasselbe ohne Besitzanspruch: es sind Moeglichkeiten,
     *    keine Posten.
     */
    h.textContent = "Wege";
    const reihe = document.createElement("div");
    reihe.className = "tagesbild-reihe";
    /*
     * Und der zweite Teil von Martins Satz, der leicht untergeht: *„Es soll
     * mir der Hinweis sein, dass man aktiv auch was arbeiten kann, wenn man
     * etwas challenging haben moechte."* Die Zeile muss also BEIDES sagen —
     * nichts ist faellig, und hier faengt man an, wenn man will.
     *
     * ⚠️ Sie entsteht HIER (blickTagesbild), nicht beim Fuellen. Das Fuellen
     *    laeuft nach jeder Antwort erneut (erneuereTagesbild) — dort gebaut
     *    stuende sie beim zweiten Mal doppelt.
     */
    // 23.09.2026: Der Satz „Nichts davon ist fällig …" steht nicht mehr
    // darunter — der freigegebene Entwurf zeigt die Wege ohne Zusatz.
    sec.append(h, reihe);
    void this.fuelleTagesbild(sec, reihe);
    return sec;
  }

  /**
   * Das Tagesbild neu holen — nach einer Antwort (Ereignis) und bei jedem
   * refresh(). Vorher stand es vom ersten Aufbau der Seite, und wer eine
   * tiefe Frage beantwortete und zurückkam, sah „Frage" noch offen.
   */
  /**
   * Eine erledigte Zeile aus der offenen Liste nehmen — sofort.
   *
   * ⚠️ Bewusst LOKAL, ohne neue Ladung. Der Server liefert die Zeile schon
   *    nicht mehr (`/api/offen` kennt nur unbeantwortete Fragen); alles,
   *    was fehlte, war das Wegnehmen auf dem Schirm. Eine ganze Ladung
   *    nachzubauen hiesse, Tickets, Wetter und Alltag mitzuholen — für das
   *    Streichen einer Zeile.
   */
  private streicheOffen(praefix: string): void {
    const vorher = this.letzteTun.length;
    this.letzteTun = this.letzteTun.filter((z) => !z.id.startsWith(praefix));
    if (this.letzteTun.length !== vorher) this.fuelleOffen(this.letzteTun);
  }

  private erneuereTagesbild(): void {
    const sec = this.root.querySelector<HTMLElement>('[data-leiste="tagesbild"]');
    const reihe = sec?.querySelector<HTMLElement>(".tagesbild-reihe");
    if (sec && reihe) void this.fuelleTagesbild(sec, reihe);
  }

  private async fuelleTagesbild(
    sec: HTMLElement,
    reihe: HTMLElement,
  ): Promise<void> {
    try {
      const r = await fetch("/api/tagesbild", { credentials: "same-origin" });
      if (!r.ok) return;
      const d = (await r.json()) as {
        felder?: Array<{
          schluessel: string;
          wort: string;
          getan?: string;
          erfuellt: boolean;
          /** Zaehlende Felder: wie weit, wie weit noch. Seit 06.09. beim Impuls. */
          stand?: number;
          ziel?: number;
          /** Nur die Meditation (27.09.): liegt heute eine fertige bereit? */
          bereit?: boolean;
        }>;
        rund?: boolean;
      };
      if (!d.felder?.length) return;
      reihe.replaceChildren();
      /*
       * 05.09.2026 — die vier Punkte sind zugleich die Schnellwege.
       *
       * Martin: *„Es wäre gut, wenn ich auf der Zuhauseseite einen
       * Schnellbutton für den Spiegel hätte, weil der sehr schwer zu
       * erreichen ist. Bitte suche dazu den geeigneten Platz."*
       *
       * Der Platz war schon da: „Dein Tag" nennt Spiegel, Frage, Impuls und
       * Aufgabe ohnehin beim Namen. Ein Punkt, der sagt „Spiegel: noch
       * offen", ist der natürlichste Weg dorthin — ein fünftes Element
       * daneben wäre eine Wiederholung gewesen.
       */
      const wege: Record<string, (erfuellt: boolean) => void> = {
        spiegel: () => oeffneZiel("spiegel"),
        erinnerung: () => oeffneZiel("tickets"),
        /*
         * 06.09.: Ist die Frage beantwortet, zeigt der Punkt die Antwort des
         * Tages zum Nachlesen (Martin: „sodass ich mich über den Tag noch an
         * das erinnere"); sonst führt er zur offenen Frage.
         */
        frage: (erfuellt) =>
          void import("./deep-questions-view.js").then((m) =>
            (erfuellt ? m.zeigeAntwortDesTages() : m.oeffneOffeneFrage()).then((ok) => {
              if (!ok) oeffneZiel("tickets");
            }),
          ),
        heute: () => {
          // Die Karte wohnt in „Heute" — ist ein anderer Bereich offen, erst
          // dorthin, sonst liefe der Sprung ins Leere.
          this.zeigeBereich("heute");
          this.root
            .querySelector("[data-leiste=heute]")
            ?.scrollIntoView({ behavior: sanft(), block: "center" });
        },
        /*
         * ⚠️⚠️ 18.09.2026 — DER FUENFTE PUNKT HATTE KEINEN WEG.
         *
         * Martin: *„Zuhause auf Tugend Click passiert gar nichts. Oben bei
         * den Schnell-Buttons."*
         *
         * Das Tagesbild liefert FUENF Felder (spiegel, frage, tugend, heute,
         * erinnerung), diese Tabelle kannte VIER. Der Tugend-Punkt sah aus
         * wie die anderen, war anklickbar — und tat nichts.
         *
         * ⚠️ Zum zweiten Mal dieselbe Sache: Am 16.09. meldete Martin schon
         *    einmal „auf Tugend geklickt, da passiert nichts". Damals fehlte
         *    der Mauszeiger an der Zeile weiter unten. Der Punkt HIER oben
         *    hatte nie einen Weg — die damalige Reparatur betraf eine andere
         *    Stelle, und niemand hat nachgezaehlt.
         *
         * Der Weg ist derselbe wie beim Impuls: zum Abschnitt springen, den
         * es im Zuhause ohnehin gibt. Eine eigene Ansicht fuer eine Tugend
         * des Tages waere zu viel fuer einen Satz.
         */
        tugend: () => {
          this.zeigeBereich("heute");
          this.root
            .querySelector("[data-leiste=tugend]")
            ?.scrollIntoView({ behavior: sanft(), block: "center" });
        },
        // 27.09.2026 (Ticket 2636): der sechste Weg — siehe oeffneMeditationsWeg.
        meditation: () => void this.oeffneMeditationsWeg(),
      };
      for (const f of d.felder) {
        const feld = document.createElement("button");
        feld.type = "button";
        feld.className = "tagesbild-feld";
        const weg = wege[f.schluessel];
        if (weg) feld.addEventListener("click", () => weg(f.erfuellt));
        // ⚠️ Kein `aria-label` mit „nicht erfüllt" — auch die Ansage bleibt
        //    ohne Urteil. Der Zustand steht im data-Attribut fürs Blatt.
        feld.dataset.erfuellt = f.erfuellt ? "ja" : "nein";
        const wort = document.createElement("span");
        wort.className = "tagesbild-wort";
        wort.textContent = f.wort;
        /*
         * 27.09.2026 (Ticket 2636, Entwurf B „eine Tafel", freigegeben:
         * „Alles sehr schön … du kannst loslegen"): jeder Weg trägt sein
         * feines Zeichen in einem Ring. Erfüllt: Ring im Akzent mit Hauch und
         * einer kleinen Plakette mit Haken unten rechts. Wachsend: ein Bogen
         * auf dem Ring. Die Meditation, wenn sie heute bereit ist: ein Punkt
         * oben rechts.
         *
         * Was von früher BLEIBT: keine zweite Zeile unter dem Wort (Martin
         * 23.09.: „überladen"), keine Zahl auf der Kachel (06.09.), das Verb
         * nur in Ansage und Tooltip.
         */
        const ring = document.createElement("span");
        ring.className = "tagesbild-ring";
        ring.setAttribute("aria-hidden", "true");
        ring.innerHTML = zeichen(wegZeichenName(f.schluessel), 22);
        /*
         * 06.09.2026 — der Impuls waechst mit.
         *
         * Martin: *„dass ich mindestens drei Impulse am Tag haben moechte,
         * bis er dann ganz gruen ist. Das heisst, er waechst immer weiter."*
         *
         * Der Ring fuellt sich als Bogen (CSS: conic-gradient ueber
         * `--anteil`, zum Ring ausgestanzt), die Zahl selbst steht nur in
         * der Ansage — auf der Kachel bliebe „1 von 3" ein Vorwurf. Felder
         * ohne Ziel bleiben, was sie waren: an oder aus.
         */
        const ziel = Number(f.ziel ?? 0);
        const stand = Math.max(0, Math.min(ziel, Number(f.stand ?? 0)));
        if (ziel > 1) {
          ring.dataset.wachsend = "ja";
          ring.style.setProperty("--anteil", String(stand / ziel));
        }
        if (f.erfuellt) {
          const haken = document.createElement("span");
          haken.className = "tagesbild-haken";
          haken.innerHTML = zeichen("haken", 10);
          ring.append(haken);
        } else if (f.bereit) {
          ring.dataset.bereit = "ja";
        }
        const geschehen = f.erfuellt || stand > 0;
        feld.dataset.geschehen = geschehen ? "ja" : "nein";
        feld.append(ring, wort);
        /*
         * ⚠️ 27.09.2026: Die Meditation ohne fertige Datei bleibt ein Knopf
         *    (kein Loch in der Tafel) und sagt ehrlich, dass es heute keine
         *    gibt — sie führt dann nach „Nebenbei", ohne etwas zu starten.
         */
        const ansage = f.erfuellt
          ? `${f.wort} ${f.getan ?? "erledigt"}`
          : f.bereit === false
            ? `${f.wort} — heute keine fertige`
            : f.bereit
              ? `${f.wort} anhören`
              : ziel > 1 && stand > 0
                ? `${f.wort} öffnen — ${stand} von ${ziel} heute`
                : `${f.wort} öffnen`;
        feld.setAttribute("aria-label", ansage);
        feld.title = ansage;
        reihe.append(feld);
      }
      sec.dataset.rund = d.rund ? "ja" : "nein";
      sec.hidden = false;
    } catch {
      /* Eine Uebersicht darf nie an einem Zaehler scheitern. */
    }
  }

  /**
   * Was du dir heute vornimmst. (07.09.2026, Ticket #2141)
   *
   * Aus dem Gespraech zwischen Vespera und Maja: Der Spiegel soll nicht nur
   * festhalten, was war, sondern den Kontrast zwischen der Absicht und dem
   * tatsaechlichen Verlauf zeigen — wertfrei, als Angebot, nicht als Urteil.
   *
   * ⚠️ Bewusst KEIN Haken und keine Erfolgsquote. Eine Absicht, die man
   *    abhaken kann, ist eine Aufgabe; der Spiegel soll sie am Abend
   *    betrachten, nicht bewerten.
   */
  private blickAbsicht(): HTMLElement {
    /*
     * ⚠️ 23.09.2026 — EINE ZEILE. Martin: *„Das Feld ‚Absicht' kann man auch
     *    irgendwie anders darstellen. Das ist viel zu viel Information mit
     *    ‚Absicht', ‚Eingabefeld', ‚Mikrofon', ‚Merken' und ‚Vorschlagen'."*
     *    Jetzt: eine kleine Überschrift, ein Feld mit dem Mikrofon darin,
     *    Enter merkt (der Knopf „Merken" bleibt im Baum, ist aber nicht
     *    mehr zu sehen), und „Vorschlag aus dem Spiegel holen" als leiser
     *    Link. Die Absicht steht unter „Nebenbei" — sie wurde in zwei
     *    Wochen dreimal benutzt, sie gehört nicht nach oben.
     */
    const sec = document.createElement("section");
    sec.className = "zuhause-absicht";
    sec.dataset.leiste = "absicht";
    const h = document.createElement("h3");
    h.className = "zuhause-absicht-titel";
    h.textContent = "Absicht für heute";

    const feld = document.createElement("textarea");
    feld.className = "zuhause-absicht-feld";
    feld.rows = 1;
    feld.enterKeyHint = "done";
    feld.maxLength = 500;
    feld.placeholder = "Was nimmst du dir heute vor?";
    feld.setAttribute("aria-label", "Deine Absicht für heute");
    /*
     * ⚠️ 23.09.2026 — Das Feld wächst mit dem Text, bis fünf Zeilen. Die
     *    „eine Zeile" vom selben Tag schnitt jeden längeren Satz ab (fest
     *    24 px hoch, ohne Umbruch). Martin, mit einem Vorschlag aus dem
     *    Spiegel im Feld: *„Optimieren, sonst kann ich den ganzen Text nicht
     *    lesen in dieser Form."* Leer bleibt es eine Zeile.
     */
    const anpassen = wachsendesTextfeld(feld, 5, 1);

    const satz = document.createElement("p");
    satz.className = "zuhause-absicht-satz";
    satz.hidden = true;

    const knopf = document.createElement("button");
    knopf.type = "button";
    // ⚠️ 22.09.2026 — ohne Variante nahm der Browser seine eigene Vorgabe.
    knopf.className = "btn btn--sm btn--primary zuhause-absicht-knopf";
    knopf.textContent = "Merken";
    knopf.title = "Leeres Feld nimmt die Absicht wieder weg";

    /**
     * Vorschlagen — wenn keine Absicht kommt. (10.09.2026)
     *
     * Martin: *„damit das System aus eigenen Recherchen eine Absicht benennen
     * kann, wenn mir keine einfällt."*
     *
     * ⚠️ Der Vorschlag landet im FELD, nicht in der Absicht. Gemerkt wird er
     *    erst, wenn Martin daneben tippt — eine Absicht, die sich selbst
     *    setzt, waere keine mehr.
     */
    const vorschlagen = document.createElement("button");
    vorschlagen.type = "button";
    vorschlagen.className = "zuhause-text-knopf zuhause-absicht-vorschlag";
    vorschlagen.textContent = "Vorschlag aus dem Spiegel holen";
    vorschlagen.title = "Aus deinem letzten Spiegel und dem, was offen ist";

    const bearbeiten = document.createElement("button");
    bearbeiten.type = "button";
    bearbeiten.className = "zuhause-absicht-bearbeiten";
    bearbeiten.textContent = "ändern";
    bearbeiten.hidden = true;

    const hinweis = document.createElement("p");
    hinweis.className = "zuhause-absicht-hinweis";
    hinweis.hidden = true;

    /** Gesetzt: der Satz steht. Offen: das Feld steht. */
    const zeige = (text: string | null): void => {
      const da = !!text?.trim();
      satz.textContent = text ?? "";
      satz.hidden = !da;
      bearbeiten.hidden = !da;
      zeile.hidden = da;
      knopf.hidden = da;
      vorschlagen.hidden = da;
      knopfreihe.hidden = da;
      // Ein laufendes Diktat endet, wenn das Feld verschwindet.
      if (da) mikro.beenden();
      if (!da) {
        feld.value = "";
        anpassen();
      }
    };

    bearbeiten.addEventListener("click", () => {
      const alt = satz.textContent ?? "";
      zeige(null);
      feld.value = alt;
      anpassen();
      feld.focus();
    });

    const merken = async (): Promise<void> => {
      const text = feld.value.trim();
      knopf.disabled = true;
      hinweis.hidden = true;
      try {
        /*
         * Leeres Feld heisst wegnehmen. Eine Absicht ist kein Vertrag — wer
         * mittags merkt, dass sie nicht stimmt, streicht sie, statt bis
         * Mitternacht mit ihr zu leben.
         */
        const r = text
          ? await fetch("/api/tagesabsicht", {
              method: "POST",
              credentials: "same-origin",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ text }),
            })
          : await fetch("/api/tagesabsicht", {
              method: "DELETE",
              credentials: "same-origin",
            });
        if (!r.ok) throw new Error(String(r.status));
        zeige(text || null);
      } catch {
        // Kein Toast: der Text steht noch im Feld, nichts ist verloren.
        hinweis.textContent = "Konnte nicht gespeichert werden. Nochmal tippen?";
        hinweis.hidden = false;
      } finally {
        knopf.disabled = false;
      }
    };
    knopf.addEventListener("click", () => void merken());

    const holeVorschlag = async (): Promise<void> => {
      vorschlagen.disabled = true;
      const vorher = vorschlagen.textContent;
      vorschlagen.textContent = "Denke nach …";
      hinweis.hidden = true;
      try {
        const r = await fetch("/api/tagesabsicht/vorschlag", {
          method: "POST",
          credentials: "same-origin",
        });
        const j = (await r.json()) as {
          ok?: boolean;
          text?: string;
          message?: string;
        };
        if (j.ok && j.text) {
          feld.value = j.text;
          anpassen();
          feld.focus();
          // Ans Ende, damit man sofort weiterschreiben kann.
          feld.setSelectionRange(feld.value.length, feld.value.length);
        } else {
          hinweis.textContent = j.message ?? "Mir fällt gerade nichts ein.";
          hinweis.hidden = false;
        }
      } catch {
        hinweis.textContent = "Der Vorschlag hat gerade nicht geklappt.";
        hinweis.hidden = false;
      } finally {
        vorschlagen.disabled = false;
        vorschlagen.textContent = vorher ?? "Vorschlag aus dem Spiegel holen";
      }
    };
    vorschlagen.addEventListener("click", () => void holeVorschlag());
    // Enter merkt, Umschalt+Enter macht eine neue Zeile.
    feld.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        void merken();
      }
    });

    /*
     * 08.09.2026 — Martin: *„Wenn ich mit dem Cursor reingehe, poppt zwar
     * die Tastatur auf, aber ich kann nicht reinsprechen. Bitte mit
     * Mikrofon auch einsprechbar machen, so wie bei anderen Elementen."*
     *
     * ⚠️ Der Knopf ist NICHT hier gebaut, sondern in lib/feld-mikrofon.ts —
     *    es gab ihn schon einmal (Bild-Beschriftung), und beim dritten Feld
     *    waere er dreimal abgeschrieben gewesen.
     */
    const mikro = haengeMikrofonAn(feld, {
      klasse: "feld-mikro zuhause-absicht-mikro",
      beiText: anpassen,
    });

    const zeile = document.createElement("div");
    zeile.className = "zuhause-absicht-zeile";
    zeile.append(feld, mikro.knopf);

    const liste = document.createElement("div");
    liste.className = "zuhause-liste zuhause-absicht-liste";
    // Die zwei Knoepfe stehen nebeneinander: merken ist die Tat,
    // vorschlagen der Umweg dorthin.
    const knopfreihe = document.createElement("div");
    knopfreihe.className = "zuhause-absicht-knopfreihe";
    knopfreihe.append(knopf, vorschlagen);
    liste.append(satz, bearbeiten, zeile, knopfreihe, hinweis);
    sec.append(h, liste);

    // Was heute schon steht, holen — ohne die Seite aufzuhalten.
    void (async () => {
      try {
        const r = await fetch("/api/tagesabsicht", { credentials: "same-origin" });
        if (!r.ok) return;
        const j = (await r.json()) as { absicht?: { text?: string } | null };
        const text = j.absicht?.text?.trim();
        if (text) zeige(text);
      } catch {
        /* kein Netz — dann eben das leere Feld */
      }
    })();

    return sec;
  }

  /**
   * Der Fund des Tages. (08.09.2026)
   *
   * Martin am 07.09.: *„Kann Vespera einmal am Tag zumindest einen wichtigen
   * Beitrag liefern, wie zum Beispiel einen Blick auf die Geschichte meines
   * Großvaters? … weil ich sehr interessiert finde, dort hineinzugehen in
   * diese Themen."*
   *
   * ⚠️ Der Kasten steht nur da, wenn es einen Fund gibt. Eine Zeile „heute
   *    noch keiner" waere eine Ankuendigung, und Ankuendigungen sind das,
   *    was diese Seite nicht tut — dieselbe Regel wie beim Impuls.
   */
  private blickFund(): HTMLElement {
    /*
     * ⚠️ 23.09.2026 — der Fund wohnt im RÜCKBLICK. Martin: *„Was mache ich
     *    bitte mit ‚Fund des Tages‘? Der ist im Zuhause viel zu präsent."*
     *    Er ist ein Text über Martins Leben, nicht über heute — also dort,
     *    wo das Gewesene steht. In „Heute" bleibt nur ein leiser Hinweis
     *    unter „Nebenbei" (siehe blickNebenbei), der hierher wechselt.
     */
    const sec = document.createElement("section");
    sec.className = "zuhause-fund";
    sec.dataset.leiste = "fund";
    sec.hidden = true;
    const h = document.createElement("h2");
    h.className = "zuhause-blick-titel";
    h.textContent = "Fund des Tages";
    const karte = document.createElement("article");
    karte.className = "zuhause-karte zuhause-fund-karte";
    sec.append(h, karte);
    // Gefüllt von fuelleFund() — aus refresh(), je Wiener Tag einmal.
    return sec;
  }

  /**
   * Den Fund des Tages holen: die Karte im Rückblick füllen und die Daten
   * an das Angebot in „Nebenbei" geben (Ticket 2561).
   *
   * ⚠️ Der Hinweis in „Heute" wird hier NICHT mehr gesetzt — das tut nur
   *    zeigeAngebot(). Tagsüber ersetzt der Fund als Angebot den Hinweis;
   *    zwei Stellen, die beide `hidden` setzen, wären zwei Besitzer.
   */
  private async fuelleFund(): Promise<void> {
    const sec = this.root.querySelector<HTMLElement>('[data-leiste="fund"]');
    const karte = sec?.querySelector<HTMLElement>(".zuhause-fund-karte");
    if (!sec || !karte) return;
    this.fundGeholtFuer = wienerTag(new Date());
    try {
      const r = await fetch("/api/fund/heute", { credentials: "same-origin" });
      const d = (await r.json()) as {
        fund?: {
          titel?: string;
          text?: string;
          tag?: string;
          gelesenAm?: string;
          herkunft?: { zeilen: number; eigene: number; von: string; bis: string };
        } | null;
      };
      const f = d.fund;
      /*
       * ⚠️ Kein Fund heißt NICHT „gleich nochmal": der erste Aufruf des Tages
       *    erzeugt ihn beim starken Modell. Nach 15 Minuten wird erneut
       *    gefragt, höchstens dreimal je Tag (FUND_NEU_*), damit nicht jeder
       *    Blick auf die Seite einen neuen Lauf auslöst.
       */
      if (!f?.titel || !f.text) {
        const tag = wienerTag(new Date());
        if (this.fundNachfragen.tag !== tag) this.fundNachfragen = { tag, zahl: 0 };
        if (this.fundNachfragen.zahl < FUND_NEU_HOECHSTENS) {
          this.fundNachfragen.zahl++;
          this.fundGeholtFuer = "";
          this.fundWiederAb = Date.now() + FUND_NEU_NACH_MS;
        }
        return;
      }
      const titel = f.titel;
      const text = f.text;
      const oeffne = (abspielen: boolean): void => {
        void import("./fund-blatt.js").then((m) =>
          m.zeigeFund({ titel, text, herkunft: f.herkunft, abspielen }),
        );
      };
      const kopf = document.createElement("span");
      kopf.className = "zuhause-fund-titel";
      kopf.textContent = titel;
      const herkunft = document.createElement("p");
      herkunft.className = "zuhause-fund-herkunft";
      herkunft.textContent = f.herkunft
        ? `Ein Faden aus deinem Leben — aus ${f.herkunft.zeilen} Einträgen, ${f.herkunft.eigene} davon in deinen eigenen Worten.`
        : "Ein Faden aus deinem Leben.";
      if (f.gelesenAm) herkunft.textContent += " Gelesen.";
      const wege = document.createElement("div");
      wege.className = "zuhause-fund-wege";
      const lesen = document.createElement("button");
      lesen.type = "button";
      lesen.className = "zuhause-text-knopf";
      lesen.textContent = "Lesen";
      lesen.addEventListener("click", () => oeffne(false));
      const hoeren = document.createElement("button");
      hoeren.type = "button";
      hoeren.className = "zuhause-text-knopf";
      hoeren.textContent = "Anhören";
      hoeren.addEventListener("click", () => oeffne(true));
      wege.append(lesen, hoeren);
      karte.replaceChildren(kopf, herkunft, wege);
      sec.hidden = false;
      this.angebotDaten.fund = { titel, text, ...(f.tag ? { tag: f.tag } : {}) };
      this.zeigeAngebot();
    } catch {
      /* Ohne Fund bleibt der Kasten weg — kein Platzhalter. */
      this.fundGeholtFuer = "";
    }
  }

  private blickTugend(): HTMLElement {
    /*
     * ⚠️ 23.09.2026 — „Haltung des Tages" als Karte (Entwurf, freigegeben).
     *    Der Name trägt die Karte, die Familie steht darunter, der Knopf
     *    rechts spielt sie ab. Die GANZE Karte ist antippbar — am Telefon
     *    steht dort gar kein Knopf, und zweimal schon (16.09., 18.09.) hat
     *    ein Tipp auf die Tugend nichts getan.
     */
    /*
     * ⚠️ 23.09.2026 abends — WIEDER „TUGEND", UND DER SATZ STEHT DA (#2545).
     *    Martin: *„Ich sehe die Tugend nicht mehr in meinem Zuhause und wenn
     *    ich auf den Button oben Tugend klicke, dann springt er zwar runter
     *    ein Stück, aber die Tugend ist verschwunden."* Seit dem Umbau am
     *    Morgen hieß die Karte „Haltung", und ihr Satz stand nur noch im
     *    Tooltip — den gibt es am Telefon nicht. Der Knopf oben hieß weiter
     *    „Tugend" und sprang auf eine Karte, die das Wort nicht trug.
     */
    const sec = document.createElement("section");
    sec.className = "zuhause-tugend zuhause-karte";
    sec.dataset.leiste = "tugend";
    const h = document.createElement("h2");
    h.className = "zuhause-blick-titel";
    h.append("Tugend");
    const breit = document.createElement("span");
    breit.className = "zuhause-nur-breit";
    breit.textContent = " des Tages";
    h.append(breit);
    const heute = new Date().toLocaleDateString("en-CA", {
      timeZone: "Europe/Vienna",
    });
    const t = tugendDesTages(heute);
    const beruehre = (): void => {
      /*
       * 08.09.2026 — das Antippen IST das Anfassen.
       *
       * Martin: *„Bei Dein Tag steht, dass schon ein Teil davon gemacht
       * ist, aber ich habe noch nicht mal begonnen, es anzuhoeren."*
       * Gezaehlt wurde bis heute das Anzeigen — gestempelt vom
       * naechtlichen Bereitschafts-Ping. Jetzt zaehlt, was er tut.
       */
      void fetch("/api/tugend/beruehrt", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      })
        .then(() => {
          // Der Stand im Tagesbild soll sofort nachziehen.
          window.dispatchEvent(new CustomEvent("voice-v8:tagesbild-frisch"));
        })
        .catch(() => undefined);
      // Derselbe Weg wie ueberall: die Frage geht in den Chat, nicht in
      // ein eigenes Blatt. Ein Modul weniger, ein Weg mehr.
      window.dispatchEvent(
        new CustomEvent("voice-v8:ask-vespera", {
          detail: {
            text:
              `Die Tugend heute ist „${t.name}" — ${t.text} ` +
              `Was heißt das konkret für meinen Tag?`,
          },
        }),
      );
    };
    const reihe = document.createElement("div");
    reihe.className = "zuhause-haltung-reihe";
    const text = document.createElement("div");
    text.className = "zuhause-haltung-text";
    const name = document.createElement("span");
    name.className = "zuhause-haltung-name";
    name.textContent = t.name;
    const familie = document.createElement("span");
    familie.className = "zuhause-haltung-familie";
    // 02.09.2026 — Martin: „dass jede Tugend auch ein Symbol bekommt". Die
    // FAMILIE trägt das Zeichen (10 statt 101); klein, vor ihrem Namen.
    familie.innerHTML = zeichen(tugendZeichenName(t.kategorie), 14);
    familie.append(t.kategorie);
    text.append(name, familie);
    const knopf = document.createElement("button");
    knopf.type = "button";
    knopf.className = "zuhause-rund-knopf";
    knopf.setAttribute("aria-label", `Tugend „${t.name}" anhören`);
    knopf.title = "Anhören";
    knopf.innerHTML = zeichen("abspielen", 16);
    knopf.addEventListener("click", (ev) => {
      ev.stopPropagation();
      beruehre();
    });
    reihe.append(text, knopf);
    const satz = document.createElement("p");
    satz.className = "zuhause-haltung-satz";
    satz.textContent = ohneQuelle(t.text);
    sec.addEventListener("click", beruehre);
    sec.append(h, reihe, satz);
    return sec;
  }

  /**
   * Holt den Stand der Morgenmeditation. (23.09.2026, Ticket 2549)
   *
   * Martin: *„Wenn ich in der Früh munter werde und alles ist fertig
   * ausgearbeitet und die Morgenmeditation läuft."* Nachts entsteht sie aus
   * seinem Tag (server/morgen-meditation.ts).
   *
   * ⚠️ Seit Ticket 2561 keine eigene Karte mehr: sie ist morgens das Angebot
   *    in „Nebenbei". Hier werden nur die Daten geholt; gezeichnet wird in
   *    zeigeAngebot(). Ohne fertige Datei kein Angebot, kein Platzhalter.
   */
  private async fuelleMeditation(): Promise<void> {
    try {
      const r = await fetch("/api/morgenmeditation", { credentials: "same-origin" });
      const d = (await r.json()) as AngebotDaten["meditation"];
      this.angebotDaten.meditation = d ?? null;
      this.zeigeAngebot();
    } catch {
      /* Ohne Meditation bleibt das Angebot, wie es ist — kein Platzhalter. */
    }
  }

  /**
   * Der Weg „Meditation" auf der Tafel. (27.09.2026, Ticket 2636)
   *
   * ⚠️ EIN Player, EIN Besitzer: Kein zweiter Player unter der Tafel. Der Weg
   *    merkt den Start (`gestartet` — damit hält lib/tagesangebot.ts die
   *    Meditation auch tagsüber und abends in „Nebenbei"), lässt
   *    zeigeAngebot() die Stelle zeichnen und löst DEREN Knopf aus: derselbe
   *    Start samt Fokusführung wie beim Antippen dort.
   * ⚠️ Ohne fertige Meditation führt der Knopf trotzdem hin, nach
   *    „Nebenbei", ohne zu starten — nie ein Knopf, der nichts tut (Vorfall
   *    18.09., siehe die Weg-Tabelle in fuelleTagesbild).
   */
  private async oeffneMeditationsWeg(): Promise<void> {
    // Die Tafel kann „bereit" melden, während die Daten des Angebots noch
    // von vorhin sind (App über Nacht offen) — dann einmal nachfragen.
    if (!meditationHeute(new Date(), this.angebotDaten.meditation)) {
      await this.fuelleMeditation();
    }
    this.zeigeBereich("heute");
    const m = this.angebotDaten.meditation;
    const stelle = this.root.querySelector<HTMLElement>('[data-slot="angebot"]');
    if (!meditationHeute(new Date(), m) || !stelle) {
      // Nach dem Warten aufs Netz: hat er inzwischen selbst gescrollt, nicht dagegen ziehen (lib/scroll-ruhe.ts).
      if (!nutzerScrolltGerade()) {
        this.root
          .querySelector("[data-leiste=nebenbei]")
          ?.scrollIntoView({ behavior: sanft(), block: "center" });
      }
      return;
    }
    const knopf = (): HTMLButtonElement | null =>
      stelle.dataset.art === "meditation"
        ? stelle.querySelector<HTMLButtonElement>(".zuhause-angebot-knopf")
        : null;
    // Läuft sie schon (Knopf weg, Player da), nur hinführen — ein zweiter
    // Klick risse den laufenden Player ab.
    if (!knopf()?.hidden) {
      this.angebotDaten.gestartet = { tag: m.tag, um: Date.now(), dauerSek: m.dauerSek ?? null };
      this.zeigeAngebot();
      knopf()?.click();
    }
    if (!nutzerScrolltGerade()) stelle.scrollIntoView({ behavior: sanft(), block: "center" });
  }

  /**
   * Das EINE Angebot in „Nebenbei" zeichnen — und den Fund-Hinweis darunter.
   * (24.09.2026, Ticket 2561)
   *
   * Martin zum Entwurf: *„Der Entwurf ist sehr schön."* Die Meditation stand
   * den ganzen Tag ganz oben, mit gefülltem Knopf, und wirkte wie eine
   * Aufgabe. Jetzt eine leise Stelle, die mit der Tageszeit wechselt:
   * morgens die Meditation, tagsüber der Fund, abends die Dankbarkeit.
   *
   * ⚠️ EIN Besitzer: nur hier werden Inhalt und `hidden` der Stelle und des
   *    Fund-Hinweises gesetzt. Entschieden wird rein in lib/tagesangebot.ts.
   * ⚠️ Läuft oft (jede Minute mit der Uhr, beim Sichtbarwerden, nach jedem
   *    Laden) — gleicher Schlüssel baut NICHTS neu, sonst risse ein
   *    laufender Player mitten in der Meditation ab.
   */
  private zeigeAngebot(): void {
    const stelle = this.root.querySelector<HTMLElement>('[data-slot="angebot"]');
    const zeit = this.root.querySelector<HTMLElement>('[data-slot="angebot-zeit"]');
    if (!stelle || !zeit) return;
    const wahl = waehleNebenbei(new Date(), this.angebotDaten);
    const a = wahl.angebot;
    if (!a) {
      stelle.hidden = true;
      stelle.replaceChildren();
      zeit.hidden = true;
      zeit.replaceChildren();
      this.angebotSchluessel = "";
    } else if (a.schluessel !== this.angebotSchluessel) {
      this.angebotSchluessel = a.schluessel;
      stelle.dataset.art = a.art;
      stelle.replaceChildren(...this.baueAngebot(a));
      stelle.hidden = false;
      // An der Überschrift: „Nebenbei · ☼ heute früh" (Ticket 2561, 29.09.).
      zeit.innerHTML = zeichen(a.zeichen, 13);
      zeit.prepend(" · ");
      zeit.append(` ${a.kopf}`);
      zeit.hidden = false;
    }
    this.zeigeFundHinweis(wahl.fundHinweis);
  }

  /**
   * Titel, Satz und EIN leiser Textknopf. Der Titel ist die ERSTE Zeile —
   * die Tageszeit steht an der Überschrift (zeigeAngebot), nicht darüber.
   */
  private baueAngebot(a: Angebot): HTMLElement[] {
    const titel = document.createElement("p");
    titel.className = "zuhause-angebot-titel";
    titel.textContent = a.titel;
    const teile: HTMLElement[] = [titel];
    if (a.satz) {
      const satz = document.createElement("p");
      satz.className = "zuhause-angebot-satz";
      satz.textContent = a.satz;
      teile.push(satz);
    }
    const knopf = document.createElement("button");
    knopf.type = "button";
    knopf.className = "zuhause-angebot-knopf";
    const wort = document.createElement("span");
    wort.textContent = a.knopf;
    const bild = zeichen(a.knopfZeichen.name, a.knopfZeichen.vorn ? 14 : 16);
    if (a.knopfZeichen.vorn) {
      knopf.innerHTML = bild;
      knopf.append(wort);
    } else {
      knopf.append(wort);
      knopf.insertAdjacentHTML("beforeend", bild);
    }
    teile.push(knopf);

    if (a.art === "meditation") {
      /*
       * ⚠️ Der Player wird in app.ts gebaut (buildNarrationPlayer lebt dort)
       *    und über das Ereignis HIER hineingesetzt — der einzige Weg, nicht
       *    nachgebaut. Er spielt die nachts fertig vertonte Datei.
       * ⚠️ Kein „Besprechen" daneben (Entwurf: EIN Knopf je Angebot). Wer
       *    darüber reden will, hat die Sprechtaste unten immer da.
       */
      const spieler = document.createElement("div");
      spieler.className = "zuhause-angebot-spieler";
      spieler.hidden = true;
      knopf.addEventListener("click", () => {
        spieler.hidden = false;
        knopf.hidden = true;
        this.angebotDaten.gestartet = {
          tag: a.tag ?? "",
          um: Date.now(),
          dauerSek: this.angebotDaten.meditation?.dauerSek ?? null,
        };
        document.dispatchEvent(
          new CustomEvent("vespera:meditation-anhoeren", {
            detail: { ort: spieler, tag: a.tag ?? "" },
          }),
        );
        // Prüfbefund 24.09.: Der Knopf verschwindet, während er den Fokus
        // hat — Tastatur und VoiceOver landeten am Seitenanfang. app.ts setzt
        // den Player synchron ein; der Fokus geht auf seinen ersten Knopf.
        const ziel = spieler.querySelector<HTMLElement>("button");
        if (ziel) ziel.focus();
        else {
          spieler.tabIndex = -1;
          spieler.focus();
        }
      });
      teile.push(spieler);
    } else if (a.art === "fund") {
      knopf.addEventListener("click", () => this.zeigeBereich("rueckblick"));
    } else {
      knopf.addEventListener("click", () => {
        window.dispatchEvent(
          new CustomEvent("voice-v8:ask-vespera", { detail: { text: DANKBAR_NACHRICHT } }),
        );
      });
    }
    return teile;
  }

  /** Der leise Hinweis auf den Fund unter der Absicht — nur, wenn er nicht schon das Angebot ist. */
  private zeigeFundHinweis(titel: string | null): void {
    const hinweis = this.root.querySelector<HTMLElement>('[data-slot="fund-hinweis"]');
    if (!hinweis) return;
    if (!titel) {
      hinweis.hidden = true;
      this.fundHinweisTitel = null;
      return;
    }
    if (titel !== this.fundHinweisTitel) {
      this.fundHinweisTitel = titel;
      const links = document.createElement("span");
      const vor = document.createElement("span");
      vor.className = "zuhause-nur-breit";
      vor.textContent = "Fund des Tages: ";
      const kurz = document.createElement("span");
      kurz.className = "zuhause-nur-schmal";
      kurz.textContent = "Fund: ";
      const name = document.createElement("span");
      name.className = "zuhause-fund-hinweis-titel";
      name.textContent = titel;
      links.append(vor, kurz, name);
      const rechts = document.createElement("span");
      rechts.className = "zuhause-fund-hinweis-weg";
      rechts.innerHTML =
        '<span class="zuhause-nur-breit">im Rückblick </span><span aria-hidden="true">›</span>';
      hinweis.replaceChildren(links, rechts);
    }
    hinweis.hidden = false;
  }

  /**
   * Ticket 2262 (14.09.2026): die Frage von GESTERN aus dem Essenz-Spiegel —
   * "Impuls für morgen" aus der gestrigen Reflexion, den Tag über als
   * Begleiter. Gleicher Aufbau wie blickFund(): erst versteckt, erst bei
   * echtem Inhalt sichtbar — kein Platzhalter, wenn es keine Reflexion von
   * gestern gibt (z.B. an den ersten Tagen).
   */
  private blickMorgenfrage(): HTMLElement {
    /*
     * ⚠️ 23.09.2026 — AUS „FRAGE DES TAGES" WIRD „AUS DEINEM SPIEGEL".
     *
     *    Martin: *„Was genau soll die Frage des Tages machen? … Wenn ich da
     *    draufklicke, dann schreibt er das dort rein. Das verstehe ich
     *    nicht."* Es war nie eine Frage, sondern der Abschnitt „Impuls für
     *    heute" aus dem Spiegel von gestern — und „Frage" hieß im System
     *    noch zweimal etwas anderes (die Tiefe Frage). Der Name sagt jetzt,
     *    woher der Text kommt, und die zwei Knöpfe sagen, was ein Tipp tut:
     *    den Spiegel anhören oder darüber sprechen.
     */
    const sec = document.createElement("section");
    sec.className = "zuhause-morgenfrage zuhause-karte";
    sec.dataset.leiste = "morgenfrage";
    sec.hidden = true;
    const h = document.createElement("h2");
    h.className = "zuhause-blick-titel";
    h.append("Aus deinem Spiegel");
    const breit = document.createElement("span");
    breit.className = "zuhause-nur-breit";
    breit.textContent = " · Impuls für heute";
    h.append(breit);
    const satz = document.createElement("p");
    satz.className = "zuhause-morgenfrage-text";
    const knoepfe = document.createElement("div");
    knoepfe.className = "zuhause-karten-knoepfe";
    const anhoeren = document.createElement("button");
    anhoeren.type = "button";
    anhoeren.className = "zuhause-pille zuhause-pille--voll";
    anhoeren.innerHTML = `${zeichen("abspielen", 14)}<span>Spiegel anhören</span>`;
    anhoeren.addEventListener("click", () => {
      document.dispatchEvent(new CustomEvent("vespera:spiegel-anhoeren"));
    });
    const besprechen = document.createElement("button");
    besprechen.type = "button";
    besprechen.className = "zuhause-pille";
    besprechen.innerHTML =
      '<span class="zuhause-nur-breit">Mit Vespera besprechen</span>' +
      '<span class="zuhause-nur-schmal">Besprechen</span>';
    knoepfe.append(anhoeren, besprechen);
    sec.append(h, satz, knoepfe);

    void fetch("/api/spiegel/morgenfrage", { credentials: "same-origin" })
      .then((r) => r.json())
      .then((d: { frage?: string | null }) => {
        const frage = d.frage;
        if (!frage) return;
        satz.textContent = frage;
        besprechen.onclick = () => {
          /*
           * ⚠️⚠️ 23.09.2026 — MIT HERKUNFT, nicht nackt. Hier ging der
           *    Satz des Spiegels unverändert als Martins EIGENE Nachricht
           *    hinaus. Vespera hielt sich für angesprochen („Mm-mm. Das
           *    nehm ich so, wie es dasteht"), die Gesprächsauswertung
           *    legte daraus sieben Erinnerungen über Martin an („Der User
           *    hat entschieden, dass er heute Abend früher schlafen geht")
           *    — ein Rat des Spiegels wurde zu seiner Entscheidung. Und
           *    weil der Satz die Tugend des Tages nennt, füllte er deren
           *    Ring. Dasselbe schon am 14.09. Die Nachbarn (Tugend,
           *    Impuls, Fund) rahmen ihren Text längst — nur dieser nicht.
           */
          window.dispatchEvent(
            new CustomEvent("voice-v8:ask-vespera", {
              detail: {
                text:
                  `Aus meinem Spiegel von gestern, der Impuls für heute: „${frage}"` +
                  " — was heißt das konkret für meinen Tag?",
              },
            }),
          );
        };
        sec.hidden = false;
      })
      .catch(() => {
        /* Ohne Impuls bleibt die Karte weg — kein Platzhalter. */
      });
    return sec;
  }

  private fuelleZuletzt(titel: string | null, neben: string | null): void {
    const sec = this.root.querySelector<HTMLElement>(
      '.zuhause-zuletzt[data-leiste="zuletzt"]',
    );
    const liste = sec?.querySelector<HTMLElement>(".zuhause-liste");
    if (!sec || !liste) return;
    liste.replaceChildren();
    if (!titel) {
      sec.hidden = true;
      return;
    }
    sec.hidden = false;
    liste.append(
      baueZeile({
        zeichen: "aufnahme",
        titel,
        neben,
        klassen: ["zuhause-zeile--zuletzt"],
        beiKlick: () => oeffneZiel("alltag"),
      }),
    );
  }

  /** Refresh darf kein graues Gerüst stehen lassen — Satz statt Streifen. */
  private nimmGeruestWeg(): void {
    const leer = (liste: Element | null): boolean =>
      !!liste &&
      !liste.querySelector(".zuhause-zeile:not(.zuhause-zeile--geruest)") &&
      !liste.querySelector(".zuhause-leer-satz");
    if (leer(this.root.querySelector(".zuhause-heute .zuhause-liste"))) {
      this.fuelleHeute([]);
    }
    if (leer(this.root.querySelector(".zuhause-offen .zuhause-liste"))) {
      this.fuelleOffen(this.letzteTun);
    }
  }

  private geruestZeile(blatt: boolean): HTMLElement {
    const el = document.createElement("div");
    el.className = blatt
      ? "zuhause-zeile zuhause-zeile--teaser zuhause-zeile--geruest"
      : "zuhause-zeile zuhause-zeile--geruest";
    el.setAttribute("aria-hidden", "true");
    const strich = document.createElement("span");
    strich.className = "zuhause-geruest-strich";
    el.append(strich);
    return el;
  }

  private zeigeAltenStand(): void {
    const stand = this.liesStand();
    if (!stand) return;
    if (stand.wetterZeile) {
      this.zeigeMeta(`${formatUhr()}  ·  ${stand.wetterZeile}`, true);
    }
    if (stand.heuteTitel) {
      this.fuelleHeute([], stand.heuteTitel);
    }
    if (stand.tun.length) {
      this.letzteTun = stand.tun;
      this.fuelleOffen(stand.tun);
    }
  }

  private liesStand(): {
    heuteTitel: string | null;
    tun: ZuhauseZeile[];
    wetterZeile: string | null;
  } | null {
    try {
      const raw = sessionStorage.getItem(GERUEST_KEY);
      if (!raw) return null;
      const j = JSON.parse(raw) as {
        heuteTitel?: unknown;
        tun?: unknown;
        wetterZeile?: unknown;
      };
      const heuteTitel =
        typeof j.heuteTitel === "string" && j.heuteTitel.trim()
          ? j.heuteTitel
          : null;
      const wetterZeile =
        typeof j.wetterZeile === "string" && j.wetterZeile.trim()
          ? j.wetterZeile
          : null;
      const ziele = new Set(["alltag", "chat", "tickets"]);
      const tun: ZuhauseZeile[] = [];
      if (Array.isArray(j.tun)) {
        for (const roh of j.tun) {
          if (!roh || typeof roh !== "object") continue;
          const z = roh as { id?: unknown; titel?: unknown; ziel?: unknown };
          if (typeof z.id !== "string" || typeof z.titel !== "string") continue;
          if (typeof z.ziel !== "string" || !ziele.has(z.ziel)) continue;
          tun.push({
            id: z.id,
            titel: z.titel,
            ziel: z.ziel as ZuhauseZeile["ziel"],
          });
        }
      }
      if (!heuteTitel && tun.length === 0 && !wetterZeile) return null;
      return { heuteTitel, tun, wetterZeile };
    } catch {
      return null;
    }
  }

  private merkeStand(
    heuteTitel: string | null,
    tun: ZuhauseZeile[],
    lage: Wetterlage,
    wetterZeile: string | null,
    /**
     * 06.09.2026 — die Sonnenzeiten gehoeren mit in den Merkzettel.
     *
     * Martin: *„Beim Laden wird zuerst der Tag geladen, dann kommt erst die
     * Nacht."* Der erste Anstrich rechnete die Tageszeit ohne echte Zeiten
     * und fiel damit auf die festen 6:30 bis 20:00 zurueck. Um 20:42 hiess
     * das „Abend" — und sobald das Wetter kam, sprang das Bild auf „Nacht".
     * Ein sichtbarer Fehler, jeden Abend.
     */
    sonne?: { aufgang?: string; untergang?: string } | null,
    /**
     * 07.09.2026 — die BEWÖLKUNG gehört genauso auf den Merkzettel.
     *
     * Martin: *„Es wird geladen, dann sieht man die Sonne, und plötzlich
     * lädt ein weiteres Bild, dann ist die Sonne weg und dafür sind Wolken
     * da."*
     *
     * Derselbe Fehler wie mit den Sonnenzeiten am 06.09., eine Ebene tiefer:
     * Der erste Anstrich kannte die Lage („wolken"), aber nicht die ZAHL.
     * Ohne Zahl gilt in `gestirnSichtbar` der Zweig „ohne Messwert" — und
     * der zeichnet die Sonne. Kommt dann das Wetter mit 86 Prozent, ist sie
     * weg. Ein sichtbarer Sprung, jedes Mal.
     */
    bewoelkung?: number | null,
  ): void {
    try {
      sessionStorage.setItem(
        GERUEST_KEY,
        JSON.stringify({
          heuteTitel,
          lage,
          wetterZeile,
          sonne: sonne ?? null,
          bewoelkung: typeof bewoelkung === "number" ? bewoelkung : null,
          tun: tun.map((z) => ({ id: z.id, titel: z.titel, ziel: z.ziel })),
        }),
      );
    } catch {
      /* private mode */
    }
  }

  /**
   * Die zuletzt bekannten Sonnenzeiten — fuer den ersten Anstrich.
   *
   * Ohne sie faellt `tageszeitJetzt()` auf feste Stunden zurueck, und das
   * Bild springt, sobald das Wetter da ist (Martin, 06.09.: „beim Laden wird
   * zuerst der Tag geladen, dann kommt erst die Nacht").
   */
  private liesSzeneSonne(): { aufgang?: string; untergang?: string } | null {
    // 15.09.2026: EIN Leser fuer die gemerkten Sonnenzeiten — der Stern im
    // Kopf braucht sie auch (lib/stern.ts).
    return gemerkteSonne();
  }

  /**
   * Die zuletzt bekannte Bewölkung — für den ersten Anstrich.
   *
   * ⚠️ `null` heißt „keine Angabe", und das ist NICHT dasselbe wie 0: ohne
   *    Angabe zeichnet `gestirnSichtbar` die Sonne, bei 0 auch — aber bei 86
   *    eben nicht. Genau diese Unterscheidung fehlte.
   */
  private liesSzeneBewoelkung(): number | null {
    try {
      const raw = sessionStorage.getItem(GERUEST_KEY);
      if (!raw) return null;
      const b = (JSON.parse(raw) as { bewoelkung?: unknown }).bewoelkung;
      return typeof b === "number" && Number.isFinite(b) ? b : null;
    } catch {
      return null;
    }
  }

  private liesSzeneLage(): Wetterlage {
    try {
      const raw = sessionStorage.getItem(GERUEST_KEY);
      if (!raw) return "unbekannt";
      const lage = (JSON.parse(raw) as { lage?: unknown }).lage;
      return typeof lage === "string" && WETTERLAGEN.has(lage as Wetterlage)
        ? (lage as Wetterlage)
        : "unbekannt";
    } catch {
      return "unbekannt";
    }
  }


  /**
   * Szene und Wetter-Kachel fuellen — beide aus demselben Wetter.
   *
   * ⚠️ Wirft nie: faellt das Wetter aus, bleibt die Szene im Grundzustand und
   * die Kachel leer. Ein fehlendes Wetter darf die Startseite nicht kosten.
   */
  /**
   * Der Gruss — bei JEDEM Aufbau der Seite neu, nicht nur beim ersten.
   *
   * ⚠️ Martin, um 06:29: *„Hier steht noch guten Abend, obwohl eigentlich
   * schon guten Morgen ist. Sollte das nicht automatisch sein?"*
   *
   * Er stand in `baueGeruest()` — und die laeuft EINMAL, im Aufbau der
   * Ansicht. `refresh()` erneuerte Wetterzeile, Szene und Karten, den Gruss
   * aber nie. Bleibt die App ueber Nacht offen oder kehrt aus dem
   * Hintergrund zurueck, steht dort weiter der Abend.
   *
   * Auf seinem Foto sieht man beides nebeneinander: die Zeile darunter war
   * frisch („06:29 · 16°"), die Szene zeigte die tiefe Morgensonne — nur der
   * Gruss war von gestern. Ein Wert, der von der Uhr abhaengt, gehoert in
   * den Auffrisch-Weg, nicht in den Aufbau.
   */
  private zeigeGruss(): void {
    const hallo = this.root.querySelector<HTMLElement>("[data-slot=gruss]");
    if (!hallo) return;
    const name =
      document.getElementById("nav-user-name")?.textContent?.trim() || "";
    hallo.replaceChildren();
    try {
      /*
       * ⚠️ Das Leerzeichen nach dem Komma gehoert IN den ersten Span. Der
       * Umbruch macht CSS (display:block) — ohne das Leerzeichen liest
       * textContent "Guten Morgen,Martin", und genau so liest es auch ein
       * Screenreader vor.
       */
      const tag = name ? `${gruss()}, ` : gruss();
      hallo.append(zeileSpan(tag));
      if (name) hallo.append(zeileSpan(vorname(name), "zuhause-gruss-name"));
    } catch {
      hallo.append(zeileSpan(name ? "Hallo, " : "Hallo"));
      if (name) hallo.append(zeileSpan(vorname(name), "zuhause-gruss-name"));
    }
  }

  /** Der leise Gruß ist entschieden (gezeigt oder für heute nicht dran). */
  private willkommenErledigt = false;

  /**
   * Der leise Gruß unter der Wetterzeile — höchstens einmal je Tag, nur
   * morgens und zur Nacht, nur für den Hauptnutzer. (08.10.2026)
   *
   * Texte, Zeiten und Merker stehen in zuhause-willkommen.ts. Hier nur der
   * Einbau: eine Zeile im Kopf (`.zuhause-held`), die sanft einblendet und
   * stehen bleibt — als normaler Text, den ein Screenreader mitliest (kein
   * `aria-live`: er soll nichts unterbrechen).
   *
   * ⚠️ Ist die Marke „Hauptnutzer" noch nicht gesetzt, wird NICHTS gemerkt —
   *    der nächste Aufruf (nach dem ersten Auffrischen) versucht es erneut.
   * ⚠️ Öffentlich nur, damit der Test die Uhr vorgeben kann.
   */
  zeigeWillkommen(jetzt: Date = new Date()): void {
    if (this.willkommenErledigt) return;
    try {
      if (!istHauptnutzer()) return;
      const held = this.root.querySelector<HTMLElement>(".zuhause-held");
      if (!held) return;
      this.willkommenErledigt = true;
      const name = document.getElementById("nav-user-name")?.textContent?.trim() ?? "";
      const gruss = nimmWillkommen(jetzt, vorname(name));
      if (!gruss) return;
      const zeile = document.createElement("p");
      zeile.className = "zuhause-willkommen";
      zeile.dataset.slot = "willkommen";
      zeile.dataset.art = gruss.art;
      zeile.textContent = gruss.text;
      held.append(zeile);
    } catch {
      /* Ein Gruß, der nicht kommt, darf Zuhause nicht kosten. */
    }
  }

  /**
   * 2459: Der gewählte Avatar stand nur in den Einstellungen. Header und
   * Chat haben ihn bewusst nicht (zu voll). Zuhause ist der Ort fürs Gesicht.
   * Tippen öffnet Persönlichkeit.
   */
  private async fuelleAvatar(): Promise<void> {
    const btn = this.root.querySelector<HTMLButtonElement>("[data-slot=avatar]");
    if (!btn) return;
    const img = btn.querySelector("img");
    if (!img) return;
    try {
      const cfg = await fetchPersona();
      const url = avatarUrlAus(cfg);
      if (!url) {
        btn.hidden = true;
        return;
      }
      img.alt = cfg.botName || "Vespera";
      img.src = url;
      btn.hidden = false;
    } catch {
      btn.hidden = true;
    }
  }

  private horcheAvatar(): void {
    if (IM_TEST) return;
    window.addEventListener("voice-v8:persona-changed", () => {
      void this.fuelleAvatar();
    });
  }

  private fuelleSzene(
    lage: Wetterlage,
    zeit: ReturnType<typeof tageszeitJetzt>,
    halter = this.root.querySelector<HTMLElement>("[data-slot=szene]"),
    /*
     * ⚠️ HINTER dem Halter, nicht davor: der dritte Platz war beim Aufbau
     * schon mit dem Element belegt (Zeile 348). Ein neuer Parameter in der
     * Mitte haette dort still das Element als Sonnenzeiten gelesen — der
     * Uebersetzer hat es gefangen, im Browser waere es eine leere Bahn
     * gewesen.
     */
    sonne?: { aufgang?: string; untergang?: string } | null,
  ): void {
    if (!halter) return;
    /*
     * ⚠️ 23.09.2026 (Martin, mit Bild: „Das Bild oben ist jetzt verzerrt,
     *    warum?") — die Bäume brauchen die ECHTE Streckung des Bandes, siehe
     *    bodenStreckung in tagesszene.ts. Gemessen wird am Halter; beim
     *    ersten Anstrich hängt er oft noch nicht im Dokument (Breite 0). Dann
     *    zeichnet die Szene mit 1, und der Beobachter unten zeichnet sie neu,
     *    sobald das Band seine Größe hat — auch beim Drehen des Telefons.
     */
    this.szeneZuletzt = { lage, zeit, halter, sonne };
    this.beobachteSzene(halter);
    const streckung = this.gemesseneStreckung(halter) ?? 1;
    /*
     * ⚠️ 2026-09-04: Der Schlüssel trägt jetzt die STUNDE mit. Seit die Sonne
     * über den Tag wandert (Martin: „im Osten auf, im Westen unter"), ändert
     * sich das Bild innerhalb derselben Tageszeit — ohne die Stunde im
     * Schlüssel bliebe die Morgensonne bis Mittag an ihrer ersten Stelle
     * stehen. Genau die Art Fehler, die man erst am nächsten Tag bemerkt.
     */
    const jetzt = new Date();
    /*
     * ⚠️ Beim ERSTEN Anstrich gibt es `letztesWetter` noch nicht — dann gilt
     *    der Merkzettel. Ohne ihn stand hier `null`, und `null` heisst „ohne
     *    Messwert": die Sonne wurde gezeichnet und verschwand, sobald das
     *    Wetter kam. Siehe merkeStand.
     */
    const bewoelkung =
      this.letztesWetter?.bewoelkung ?? this.liesSzeneBewoelkung();
    // ⚠️ Die Millimeter gehoeren IN den Schluessel. Ohne sie bliebe das Bild
    //    beim alten Regen stehen, wenn sich nur die Staerke aendert — genau
    //    der Fehler, den die Bewoelkung am 07.09. schon einmal hatte.
    const regenMm = this.letztesWetter?.regenMm ?? null;
    /*
     * 07.10.2026: die Jahreszeit — eingestellt („Jahreszeit der Szene") oder
     * nach dem Kalender. Sie gehört IN den Schlüssel: sonst bliebe das Bild
     * nach dem Umstellen (oder am 1. Dezember) beim alten Laub stehen.
     */
    const jahreszeit = jahreszeitFuer(loadSettings().szeneJahreszeit, jetzt);
    // 10.10.2026: die Morgenröte gehört in den Schlüssel — mit dem Aufgang fällt der
    // Dämmerton, und das Licht im Haus geht aus; ohne das bliebe es bis zur vollen Stunde.
    const roete = morgenroete(jetzt, zeit, sonne) ? ":d" : "";
    const key = `${lage}:${zeit}:${jetzt.getHours()}:${sonne?.aufgang ?? ""}:${bewoelkung ?? "-"}:${regenMm ?? "-"}:${streckung}:${jahreszeit}${roete}`;
    if (halter.dataset.szene === key) return;
    halter.dataset.szene = key;
    this.meldeSzeneMass(halter, streckung);
    halter.innerHTML = tagesszene({
      lage,
      zeit,
      jetzt,
      sonne: sonne ?? undefined,
      bewoelkung,
      regenMm,
      bodenStreckung: streckung,
      jahreszeit,
    });
  }

  /** Was fuelleSzene zuletzt bekam — der Beobachter baut damit neu. */
  private szeneZuletzt: {
    lage: Wetterlage;
    zeit: ReturnType<typeof tageszeitJetzt>;
    halter: HTMLElement;
    sonne?: { aufgang?: string; untergang?: string } | null;
  } | null = null;
  private szeneBeobachter: ResizeObserver | null = null;
  private beobachteterHalter: HTMLElement | null = null;

  /**
   * Womit das Gerät die Szene wirklich zeichnet — einmal je Streckung.
   * (23.09.2026: Martin sah nach pre.3214 noch Verzerrung; im Nachbau bei
   * 402 Punkten griff der Ausgleich. Ohne diese Zeile ist nicht zu sagen,
   * ob er auf dem Telefon ankommt.)
   */
  private gemeldeteStreckungen = new Set<number>();
  private meldeSzeneMass(halter: HTMLElement, streckung: number): void {
    if (IM_TEST || this.gemeldeteStreckungen.has(streckung)) return;
    this.gemeldeteStreckungen.add(streckung);
    const boden = halter.querySelector<SVGElement>(".szene-boden");
    const zeichnung = boden ? Math.round(boden.getBoundingClientRect().height) : "?";
    const line = `[szene] breite=${halter.clientWidth} zeichnung=${zeichnung} streckung=${streckung} beobachter=${typeof ResizeObserver !== "undefined" ? "ja" : "nein"}`;
    void fetch("/api/debug/ptt-timing", {
      method: "POST",
      body: JSON.stringify({ line }),
      headers: { "Content-Type": "text/plain" },
      keepalive: true,
      credentials: "include",
    }).catch(() => undefined);
  }

  /** Die Streckung des Bodens, wie das Band gerade ist — null, solange es keine Größe hat. */
  private gemesseneStreckung(halter: HTMLElement): number | null {
    if (!halter.isConnected) return null;
    /*
     * ⚠️ Am gezeichneten Boden messen, nicht an --szene-zeichnung: seit dem
     *    23.09. steht dort am Telefon `calc(100vw * 160 / 390)`, und eine
     *    Eigenschaft liefert ihren Text, nicht die Zahl. Das Bild selbst
     *    weiß, wie groß es ist.
     */
    const boden = halter.querySelector<SVGElement>(".szene-boden");
    if (!boden) return null;
    const r = boden.getBoundingClientRect();
    if (!(r.width > 0) || !(r.height > 0)) return null;
    return bodenStreckung(r.width, r.height);
  }

  private beobachteSzene(halter: HTMLElement): void {
    if (this.beobachteterHalter === halter) return;
    if (typeof ResizeObserver === "undefined") return;
    this.szeneBeobachter?.disconnect();
    this.beobachteterHalter = halter;
    this.szeneBeobachter = new ResizeObserver(
      gesteuert(
        "wachen",
        () => {
          const z = this.szeneZuletzt;
          if (z?.halter !== halter) return;
          // Der Schlüssel entscheidet: gleiche Streckung → kein Neuaufbau.
          this.fuelleSzene(z.lage, z.zeit, z.halter, z.sonne);
        },
        true,
      ),
    );
    this.szeneBeobachter.observe(halter);
  }

  private zeigeSzeneUndKachel(wetter: WetterBlick | null): void {
    try {
      const lage = wetterlageAus(wetter?.beschreibung);
      // ⚠️ MIT den echten Sonnenzeiten. Ohne sie fiele die Szene auf feste
      // Stunden zurueck — und genau die zeigten um 20 Uhr noch die Sonne.
      const zeit = tageszeitJetzt(new Date(), wetter?.sonne ?? null);
      this.fuelleSzene(lage, zeit, undefined, wetter?.sonne ?? null);

      const kachel = this.root.querySelector<HTMLElement>(
        "[data-slot=wetterkachel]",
      );
      if (!kachel) return;
      if (!wetter) return;
      kachel.replaceChildren();
      const bild = document.createElement("div");
      bild.className = "zuhause-wetterkachel-bild";
      bild.innerHTML = tagesszene({
        lage,
        zeit,
        kompakt: true,
        jahreszeit: jahreszeitFuer(loadSettings().szeneJahreszeit, new Date()),
      });
      const text = document.createElement("div");
      text.className = "zuhause-wetterkachel-text";
      if (wetter.jetzt !== null) {
        const grad = document.createElement("span");
        grad.className = "zuhause-wetterkachel-grad";
        grad.textContent = `${wetter.jetzt}°`;
        text.append(grad);
      }
      const dazu = document.createElement("span");
      dazu.className = "zuhause-wetterkachel-dazu";
      dazu.textContent = [wetter.spaeter, wetter.beschreibung]
        .filter(Boolean)
        .join(", ");
      const ort = document.createElement("span");
      ort.className = "zuhause-wetterkachel-ort";
      ort.textContent = wetter.ort;
      text.append(dazu, ort);
      kachel.append(bild, text);
    } catch {
      /* Ein fehlendes Bild darf die Startseite nicht kosten. */
    }
  }

  private starteLage(): void {
    void this.fuelleLage();
    if (this.lageTakt) return;
    if (!IM_TEST) {
      /*
       * ⚠️ 17.09.2026 — dieser Schlag holt DREI Netzanfragen (busy,
       *    cli/health, runtime-modell) plus den Extension-Status. Direkt
       *    darunter stand seit jeher, eine Uhr im Hintergrund koste „Akku
       *    fuer niemanden" — und die Uhr rechnet bloss. Der teure Nachbar
       *    lief ungeprueft weiter.
       *
       *    Martin: „Das Handy ist total heiss." Gemessen: sieben Anfragen je
       *    Minute bei dunklem Display, vier davon von hier.
       */
      this.lageTakt = starteSichtbarenTakt(
        () => void this.fuelleLage(),
        60_000,
      );
      // Jede Minute die Uhr — kostenlos, weil rein gerechnet. Nur wenn die
      // Seite auch wirklich zu sehen ist: eine Uhr, die im Hintergrund
      // tickt, kostet Akku für niemanden.
      this.uhrTakt = setInterval(gesteuert("live", () => {
        if (document.visibilityState === "visible") this.frischeZeit();
      }, true), 60_000);
    }
    document.addEventListener("visibilitychange", this.beiLageSichtbar);
  }

  private beiLageSichtbar = (): void => {
    if (document.visibilityState !== "visible") return;
    // Zuerst das Billige: Uhr, Gruß und Sonnenstand stehen sofort richtig,
    // auch wenn der Server gerade langsam ist.
    this.frischeZeit();
    void this.fuelleLage();
    // Und dann der volle Stand — Wetter, Aufgaben, Offenes.
    void this.refresh();
  };

  /**
   * Was von der Uhr abhängt — ohne eine einzige Serveranfrage.
   *
   * Gruß, Uhrzeit und Sonnenstand lassen sich rechnen. Nur die Wetterworte
   * kommen vom letzten Abruf; steht der noch aus, bleibt die Zeile, wie sie
   * ist, statt kurz leer zu werden.
   */
  private frischeZeit(): void {
    try {
      this.zeigeGruss();
      // Ticket 2561: an 11:00 und 17:00 wechselt das Angebot — mit der Uhr,
      // ohne Anfrage (rein gerechnet, gleicher Schlüssel baut nichts neu).
      this.zeigeAngebot();
      if (this.letztesWetter) {
        this.zeigeSzeneUndKachel(this.letztesWetter);
        // 06.09.: Die Szene wird nur stuendlich neu gebaut; das Gestirn
        // rueckt trotzdem jede Minute weiter (Martin: „die Sonne bewegt
        // sich nicht").
        const halter = this.root.querySelector<HTMLElement>("[data-slot=szene]");
        if (halter) {
          const jetzt = new Date();
          rueckeGestirn(
            halter,
            jetzt,
            this.letztesWetter.sonne ?? undefined,
            tageszeitJetzt(jetzt, this.letztesWetter.sonne ?? null),
          );
        }
        this.zeigeMeta(`${formatUhr()}  ·  ${this.letztesWetter.zeile}`, true);
      }
    } catch {
      /* Eine Uhr, die stolpert, darf die Seite nicht mitnehmen. */
    }
  }

  private async fuelleLage(): Promise<void> {
    try {
      zeichneZuhauseLage(this.root, await mitZeit(holeZuhauseLage(), []));
    } catch {
      /* Lage ist Zusatz — Zuhause bleibt. */
    }
  }

  /**
   * Die Zeile unter dem Gruß: Uhrzeit, Ort, Wetter — und der Mond.
   *
   * Martin: *„Der Mond muss entweder abnehmen oder zunehmen … das kann man
   * übrigens auch dazuschreiben: bei Zuhause, ob wir ab- oder zunehmenden
   * Mond haben."*
   *
   * ⚠️ `mitMond` ist ein Parameter und keine Automatik: Diese Zeile trägt
   *    auch andere Texte (etwa was gerade aktiv ist), und dort wäre eine
   *    Mondphase nur Rauschen.
   */
  private zeigeMeta(text: string, mitMond = false): void {
    const el = this.root.querySelector<HTMLElement>("[data-slot=meta]");
    if (!el) return;
    let t = text.trim();
    // 06.09. (Martin): das Datum dazu — direkt hinter die Uhr.
    if (/^\d{2}:\d{2}/.test(t)) {
      const d = formatDatum();
      if (d) t = t.replace(/^(\d{2}:\d{2})/, `$1  ·  ${d}`);
    }
    if (t && mitMond) {
      try {
        t += `  ·  ${mondName(mondphase(new Date()))}`;
      } catch {
        // Der Mond ist Zugabe — eine Zeile ohne ihn ist besser als keine.
      }
    }
    el.textContent = t;
    el.hidden = !t;
  }

  /**
   * „Heute" zeigt einen Impuls, keinen Rückblick.
   *
   * ⚠️ 2026-09-03 (Martin: *„Bei ‚Heute' habe ich den Sinn noch nicht ganz
   * verstanden … es ist ja nur eine Wiederholung von einem Inhalt, der
   * sowieso passiert ist"*).
   *
   * Er hatte recht: hier stand der Teaser einer Aufnahme, die er selbst
   * gemacht hat — ein Rückblick auf dem ersten Blick des Tages.
   *
   * Jetzt steht dort etwas, das nach vorne zeigt: ein kurzer, konkreter
   * Impuls fürs laufende Zeitfenster (vier pro Tag, alle vier bis fünf
   * Stunden). Antippen bringt ihn ins Gespräch, falls er darüber reden will.
   *
   * ⚠️ Der Impuls kommt NACHgeladen und der Abschnitt bleibt sichtbar mit
   * einer ruhigen Zeile, falls er ausbleibt — eine leere Überschrift wäre
   * schlimmer als ein Rückblick.
   */
  private fuelleHeute(_zeilen: ZuhauseZeile[], sofort?: string | null): void {
    const sec = this.root.querySelector<HTMLElement>(
      ".zuhause-heute[data-leiste=heute]",
    );
    const liste = sec?.querySelector<HTMLElement>(".zuhause-liste");
    if (!sec || !liste) return;
    sec.hidden = false;
    /*
     * ⚠️ 2026-09-03 — der letzte bekannte Impuls SOFORT, ohne aufs Netz zu
     * warten. Ein bestehender Waechter haelt genau das fest („zeigt den
     * letzten Stand sofort, ohne auf die API zu warten"), und mein erster
     * Anlauf hat es gebrochen: der Abschnitt wartete auf den Abruf und stand
     * so lange leer. Ein Zuhause, das beim Oeffnen grau ist, ist kein
     * Zuhause.
     */
    // 06.09.2026 (Martin: „eine schöne Aufgabe immer vor einer
    // Zusammenfassung, die danach verschwindet"): Die Zusammenfassung kam
    // als rohe Markdown-Zeile in DIESE Liste und wurde vom Impuls wieder
    // verdrängt. Jetzt hat sie ihren eigenen Abschnitt, gerendert.
    if (sofort) this.zeigeTagesblatt(sofort);
    void fetch("/api/impuls/jetzt", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then(
        (
          d: {
            impuls?: {
              text?: string;
              art?: string;
              tugend?: string;
              thema?: string;
            } | null;
            grund?: string;
          } | null,
        ) => {
          const i = d?.impuls;
          liste.replaceChildren();
          if (!i?.text) {
            /*
             * ⚠️ Zwei verschiedene Leeren, zwei verschiedene Sätze. Nach dem
             *    Abhaken „Heute noch kein Impuls" zu schreiben, wäre falsch —
             *    es gab einen, und Martin hat ihn gemacht.
             */
            liste.append(
              leerSatz(
                d?.grund === "still"
                  ? "Erledigt. Der nächste kommt später."
                  : "Heute noch kein Impuls.",
              ),
            );
            return;
          }
          const zeile = document.createElement("div");
          zeile.className = "zuhause-tat-reihe";
          const tatText = document.createElement("button");
          tatText.type = "button";
          tatText.className = "zuhause-tat-text";
          tatText.textContent = i.text;
          tatText.dataset.art = i.art ?? "";
          tatText.title = "Mit Vespera darüber sprechen";
          zeile.append(tatText);
          tatText.addEventListener("click", () => {
              /*
               * ⚠️ 09.09.2026 — hier ging NUR der Satz mit. Martin: *„Vespera
               *    kennt den Impuls nicht … und nicht erklären können, was
               *    das eigentlich bedeutet. Somit ist der Impuls wertlos in
               *    dem Fall."* Sie bekam einen Satz vorgelegt, den sie nie
               *    geschrieben hatte, ohne zu wissen, woraus er entstand.
               *    Jetzt reist die Herkunft mit; was ein Impuls ueberhaupt
               *    ist, steht seit heute in ihrem Prompt.
               */
              const herkunft = [
                i.art ? `Art: ${i.art}` : "",
                i.tugend ? `Haltung des Tages: ${i.tugend}` : "",
                i.thema ? `Thema im Hintergrund: ${i.thema}` : "",
              ]
                .filter(Boolean)
                .join(", ");
              window.dispatchEvent(
                new CustomEvent("voice-v8:ask-vespera", {
                  detail: {
                    text:
                      `Zu deinem Impuls: „${i.text}"` +
                      (herkunft ? ` (${herkunft})` : "") +
                      " — erzähl mir mehr dazu.",
                  },
                }),
              );
          });
          /*
           * Der Haken. Martin: *„Toll wäre, wenn ich das quittieren könnte,
           * sodass die nächste gute Aufgabe kommt."*
           *
           * Derselbe Knopf wie bei den offenen Zeilen (`zuhause-faden-check`)
           * — ein zweiter Haken mit eigenem Aussehen wäre die Art von
           * Doppelung, an der diese Oberfläche schon zweimal gelitten hat.
           */
          const taten = document.createElement("div");
          taten.className = "zuhause-zeile-taten";
          const check = document.createElement("button");
          check.type = "button";
          check.className = "zuhause-rund-knopf zuhause-tat-check";
          check.setAttribute("aria-label", "Als getan markieren");
          check.title = "Gemacht";
          check.innerHTML = zeichenMarkup("haken", 18);
          check.addEventListener("click", (ev) => {
            ev.stopPropagation();
            check.disabled = true;
            void fetch("/api/impuls/quittieren", {
              method: "POST",
              credentials: "same-origin",
            })
              .then((r) => (r.ok ? r.json() : null))
              .then((a: { abgehakt?: boolean } | null) => {
                if (a?.abgehakt) {
                  liste.replaceChildren(
                    leerSatz("Erledigt. Der nächste kommt später."),
                  );
                  // Der Punkt im Tagesbild wächst sofort mit — siehe oben.
                  try {
                    window.dispatchEvent(
                      new CustomEvent("voice-v8:tagesbild-frisch"),
                    );
                  } catch {
                    /* kein window (Tests) */
                  }
                } else {
                  // Nicht gespeichert — dann bleibt er stehen, statt still
                  // zu verschwinden und nirgends zu sein.
                  check.disabled = false;
                }
              })
              .catch(() => {
                check.disabled = false;
              });
          });
          taten.append(check);
          zeile.append(taten);
          liste.append(zeile);
        },
      )
      .catch(() => {
        liste.replaceChildren(leerSatz("Heute noch kein Impuls."));
      });
  }
  private fuelleOffen(alleZeilen: ZuhauseZeile[]): void {
    /*
     * ⚠️ 23.09.2026 — „Offen" zeigt nur noch AUFGABEN. Martin: *„Dann steht
     *    da ‚Tiefe Frage des Tages wartet'. Das kann man auch weggeben, weil
     *    es ja oben ohnehin die Wege gibt, die auch schon markieren, dass
     *    eben die Frage noch nicht beantwortet ist."* Dasselbe gilt für den
     *    ungehörten Spiegel: beides zeigt die Wege-Kachel.
     */
    const zeilen = alleZeilen.filter((z) => !istWegeZeile(z));
    this.fuelleSperre = true;
    try {
      const sec = this.root.querySelector<HTMLElement>(
        ".zuhause-offen[data-leiste=tun]",
      );
      const liste = sec?.querySelector<HTMLElement>(".zuhause-liste");
      if (!sec || !liste) return;
      liste.replaceChildren();
      sec.hidden = false;
      if (zeilen.length === 0) {
        liste.append(leerSatz("Nichts liegt offen."));
        return;
      }
      const { sichtbar, weitere } = zuhauseTelefonTun(zeilen, istTelefonBlick());
      for (const z of sichtbar) {
        const zeile = document.createElement("div");
        zeile.className = "zuhause-zeile-wrap";
        const btn = baueZeile({
          zeichen: zeichenFuerOffen(z),
          // 08.09.2026 — ohne den Vorspann „Erinnerung:" (siehe dort).
          titel: titelFuerAnzeige(z.titel),
          neben: z.neben ?? null,
          klassen: istOffenFaden(z) ? ["zuhause-zeile--faden"] : [],
          beiKlick: () => {
            /*
             * 2026-08-29: Wer die Zeile antippt und den Spiegel liest, HAT ihn
             * gelesen. Bliebe die Markierung dem Haken vorbehalten, stuende
             * die Mahnung morgen wieder da — obwohl er ihn gerade gelesen hat.
             * Das waere die Art von Nachlaessigkeit, die eine Aufgabenliste
             * unglaubwuerdig macht.
             *
             * Reihenfolge bewusst so: erst oeffnen, dann melden. Das Oeffnen
             * darf nicht auf das Netz warten, und scheitert das Melden, steht
             * die Zeile morgen halt nochmal da — der harmlosere Fehler.
             */
            // Der Tag steckt in der Kennung: „spiegel:2026-08-24".
            oeffneZeile(
              z.titel,
              z.ziel,
              z.id?.startsWith("spiegel:")
                ? z.id.slice("spiegel:".length)
                : null,
              (z as { art?: string }).art,
              istOffenFaden(z),
            );
            /*
             * ⚠️ 23.09.2026 — der Tipp ÖFFNET nur noch. Hier wurde der Spiegel
             *    beim Antippen gleich als gelesen vermerkt (29.08.: „wer
             *    antippt, hat gelesen"). Martin am 23.09.: „Der Spiegel soll
             *    erst abgehakt sein, wenn ich ihn auch real angehört habe."
             *    Seit pre.3179 vermerkt der Spiegel selbst, sobald er gehört
             *    ist — dann verschwindet die Zeile beim nächsten Aufbau. Der
             *    ausdrückliche Haken daneben bleibt Martins eigene Handlung.
             */
          },
        });
        zeile.append(btn);
        /*
         * 2026-08-24 (Martin: "Ich sehe im Reiter 'Zu Hause': 'Offene
         * Erinnerung: 5 bis 10 Klimmzüge'. Sollte da nicht auch ein
         * Bestätigungsbutton sein, dass man es erledigt hat?"):
         *
         *   Den Knopf gab es — aber nur fuer offene FAEDEN (kind="thread").
         *   Erinnerungen sind kind="notification" und blieben ohne. Man konnte
         *   sie ansehen und antippen, aber nicht abhaken.
         *
         *   Folge: Sie sammelten sich. Am 23.08. lagen sieben "Klimmzüge"- und
         *   "Raus mit dir"-Erinnerungen als offene Tickets in der Liste und
         *   verdeckten die echten Aufgaben.
         *
         *   Alles auf dieser Flaeche wartet darauf, erledigt zu werden — also
         *   bekommt alles den Haken. Eine Zeile ohne Weg zum Abhaken ist eine
         *   Zeile, die nie verschwindet.
         */
        if (z.id) {
          const taten = document.createElement("div");
          taten.className = "zuhause-zeile-taten";
          const nachTuen = (
            ok: boolean | { rest: number },
            knopf: HTMLButtonElement,
            wie: "erledigt" | "ausgelassen" = "erledigt",
          ): void => {
            if (typeof ok === "object") {
              /*
               * ⚠️⚠️ 09.09.2026 — EIN HAKEN, DER NICHTS SAGT, GILT ALS KAPUTT.
               *
               * Hier wurde der Knopf nur wieder freigegeben und neu geladen.
               * Beim Stapel bleibt die Zeile aber stehen (so gewollt seit
               * 06.09.: ein Haken zaehlt herunter, statt alle zu loeschen) —
               * und ohne ein Wort dazu sieht das aus, als sei der Tipp ins
               * Leere gegangen.
               *
               * Martin: *„Zuerst ist es gestanden zweimal, jetzt nur mal
               * einmal, aber es wurde nicht als abgegolten quittiert."*
               * Beides stimmt: Es hat gezaehlt, und niemand hat es gesagt.
               *
               * ⚠️ Das Neuladen wartet bewusst, sonst wischt es die Quittung
               *    im selben Augenblick wieder weg.
               */
              /*
               * ⚠️ 11.09.2026: Hier stand nur „Abgehakt". Denselben Text
               *    bekam auch das X („Nicht gemacht") — Martin sagt, er habe
               *    es NICHT getan, und liest als Bestaetigung das Gegenteil.
               *    Der Satz war fuer den Haken geschrieben und wird von
               *    beiden Knoepfen geteilt.
               */
              const wort = wie === "ausgelassen" ? "Ausgelassen" : "Abgehakt";
              const quittung = document.createElement("span");
              quittung.className = "zuhause-zeile-quittung";
              /*
               * 14.09.2026 (Martin: „ich sehe immer noch keinen gruenen Punkt
               * als Bestaetigung"): Der Satz stand hier in Grau und las sich
               * wie eine Notiz. Der Punkt macht aus der Mitteilung eine
               * Quittung — dasselbe Zeichen wie in der Liste.
               *
               * ⚠️ Beim Auslassen („Nicht gemacht") bleibt er WEG. Gruen
               *    hiesse dort: gut gemacht — und das hat er gerade verneint.
               */
              if (wie !== "ausgelassen") {
                quittung.classList.add("zuhause-zeile-quittung--gut");
                const punkt = document.createElement("span");
                punkt.className = "quittung-punkt";
                punkt.setAttribute("aria-hidden", "true");
                quittung.appendChild(punkt);
              }
              const satz = document.createElement("span");
              satz.textContent =
                ok.rest > 1
                  ? `${wort} — noch ${ok.rest} offen`
                  : `${wort} — noch eines offen`;
              quittung.appendChild(satz);
              taten.replaceChildren(quittung);
              window.dispatchEvent(new CustomEvent("voice-v8:tickets-changed"));
              window.setTimeout(() => void this.refresh(), 1800);
              return;
            }
            if (ok) {
              zeile.remove();
              this.letzteTun = this.letzteTun.filter((x) => x.id !== z.id);
              window.dispatchEvent(new CustomEvent("voice-v8:tickets-changed"));
              if (this.letzteTun.length === 0) {
                const sec2 = this.root.querySelector<HTMLElement>(
                  ".zuhause-offen[data-leiste=tun]",
                );
                if (sec2) sec2.hidden = true;
              }
            } else {
              knopf.disabled = false;
            }
          };
          const erinnerung = istErinnerungTitel(z.titel);
          const check = document.createElement("button");
          check.type = "button";
          check.className = "zuhause-faden-check";
          check.setAttribute(
            "aria-label",
            erinnerung ? "Gemacht" : "Erledigt",
          );
          check.title = erinnerung ? "Gemacht" : "Erledigt";
          check.innerHTML = zeichenMarkup("haken", 16);
          check.addEventListener("click", async (ev) => {
            ev.stopPropagation();
            check.disabled = true;
            const ok = await erledigeOffenId(z.id, "resolved");
            nachTuen(ok, check);
          });
          taten.append(check);
          if (erinnerung) {
            const skip = document.createElement("button");
            skip.type = "button";
            skip.className = "zuhause-faden-check zuhause-faden-skip";
            skip.setAttribute("aria-label", "Nicht gemacht");
            skip.title = "Nicht gemacht";
            skip.innerHTML = zeichenMarkup("schliessen", 16);
            skip.addEventListener("click", async (ev) => {
              ev.stopPropagation();
              skip.disabled = true;
              const ok = await erledigeOffenId(z.id, "skipped");
              nachTuen(ok, skip, "ausgelassen");
            });
            taten.append(skip);
          }
          zeile.append(taten);
        }
        liste.append(zeile);
      }
      if (weitere > 0) {
        const restBtn = document.createElement("button");
        restBtn.type = "button";
        restBtn.className = "zuhause-zeile zuhause-zeile--weitere";
        restBtn.textContent = zuhauseWeitereSatz(weitere);
        restBtn.addEventListener("click", () => oeffneZiel("tickets"));
        liste.append(restBtn);
      }
    } finally {
      this.fuelleSperre = false;
    }
  }

}

/**
 * Eine Offen-Zeile oeffnen — die Tagesfrage direkt, alles andere wie bisher.
 *
 * Martin: *„wenn dort angezeigt wird, eine Frage wartet auf dich, dass ich
 * direkt dorthin klicken kann."*
 *
 * ⚠️ Erkannt wird sie jetzt an ihrer ART, nicht mehr am Titel.
 *
 * Hier stand: *„Erkannt wird sie am TITEL, nicht an einer Kennung … eine
 * Kennung dafuer zu erfinden hiesse, den Meldungs-Weg umzubauen, nur um hier
 * einen Tipp abzufangen."* Das galt, solange die Frage eine Meldung war.
 * Seit dem 05.09. ist sie ein ZUSTAND und traegt `art: "frage"` — die
 * Kennung, die damals fehlte, ist da. Der Titel bleibt als Rueckfall, damit
 * Zeilen aus der alten Zeit weiter funktionieren.
 *
 * ⚠️ Ist keine Frage offen, faellt es auf das urspruengliche Ziel zurueck —
 * ein leeres Blatt waere schlechter als der gewohnte Weg.
 */
/** Zeilen, die schon eine Wege-Kachel zeigt (Spiegel, Tiefe Frage). */
function istWegeZeile(z: ZuhauseZeile): boolean {
  const id = z.id ?? "";
  return id.startsWith("spiegel:") || id.startsWith("frage:");
}

function oeffneZeile(
  titel: string,
  ziel: ZuhauseZeile["ziel"],
  schluessel?: string | null,
  art?: string,
  /**
   * Ist das ein FADEN — ein Thema zum Besprechen, keine Aufgabe?
   *
   * ## Woher das kommt
   *
   * Martin: *„Wenn ich jetzt draufklicke, ‚Das Großvater-Muster in
   * Hollenthon beleuchten', sollte dann nicht Push-to-Talk aufgehen, damit
   * ich mit ihm darüber spreche? Stattdessen geht es auf ‚Was offen ist',
   * ‚Meldungen' und 13 Aufgaben. Das ist ja nicht Sinn und Zweck der
   * Sache."*
   *
   * Er hat recht. Ein Faden ist eine Einladung zum Reden; die Liste, in der
   * er landete, ist der Entwicklungs-Rückstand. Der Impuls macht es seit dem
   * 06.09. richtig — er öffnet das Gespräch. Der Faden tat es nicht, obwohl
   * er dieselbe Sprechblase trägt.
   */
  faden?: boolean,
): void {
  // Die Regel steht in tickets-offen.ts — eine Wahrheit, pruefbar ohne DOM.
  const handlung = handlungFuerZeile({ faden, art, titel });
  if (handlung === "gespraech") {
    try {
      window.dispatchEvent(
        new CustomEvent("voice-v8:ask-vespera", {
          detail: { text: `Lass uns über „${titel}" sprechen.` },
        }),
      );
      return;
    } catch {
      /* kein window (Tests) — dann der gewohnte Weg */
    }
  } else if (handlung === "frage") {
    void (async () => {
      const { oeffneOffeneFrage } = await import("./deep-questions-view.js");
      if (!(await oeffneOffeneFrage())) oeffneZiel(ziel, schluessel);
    })();
    return;
  }
  oeffneZiel(ziel, schluessel);
}

function istTelefonBlick(): boolean {
  return typeof matchMedia === "function" && matchMedia("(max-width: 600px)").matches;
}

/**
 * Eine Zeile oeffnen.
 *
 * @param schluessel Beim Spiegel der Tag (YYYY-MM-DD). ⚠️ OHNE ihn oeffnet
 *   sich der Spiegel von HEUTE — die Zeile sagte „Montag", das Blatt zeigte
 *   Samstag. Martin am 29.08.: „Warum kann ich nicht da einfach draufklicken
 *   und der Spiegel wird dann anhörbar?" Das Datum lag am Klick-Ort die ganze
 *   Zeit vor und wurde weggeworfen.
 */
function oeffneZiel(
  ziel: ZuhauseZeile["ziel"],
  schluessel?: string | null,
): void {
  if (ziel === "spiegel") {
    if (schluessel) {
      document.dispatchEvent(
        new CustomEvent("vespera:spiegel-tag", { detail: schluessel }),
      );
      return;
    }
    // Ohne Tag derselbe Weg wie im Menue — nicht ein zweiter daneben.
    document.getElementById("open-essence-btn")?.click();
    return;
  }
  if (ziel === "alltag") {
    document.getElementById("open-alltag-btn")?.click();
    return;
  }
  if (ziel === "tickets") {
    openTicketsModal();
    return;
  }
  document.dispatchEvent(new CustomEvent("vespera:go-chat"));
}


/**
 * Eine Zeile abhaken. Zwei Arten, ein Knopf.
 *
 * 2026-08-29: Bis heute war jede Offen-Zeile ein Ticket, und die Kennung war
 * eine Zahl. Ein ungelesener Spiegel traegt `spiegel:2026-08-28` — er wird
 * nicht "erledigt", sondern als gesehen markiert.
 */
/** true = ganz erledigt, "teil" = ein Stapel-Schritt, false = fehlgeschlagen. */
async function erledigeOffenId(
  id: string,
  status: "resolved" | "skipped" = "resolved",
): Promise<boolean | { rest: number }> {
  if (id.startsWith("spiegel:")) {
    const schluessel = id.slice("spiegel:".length);
    try {
      const r = await fetch("/api/gelesen", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ art: "spiegel", schluessel }),
      });
      return r.ok;
    } catch {
      return false;
    }
  }
  const n = Number(id);
  if (!Number.isFinite(n)) return false;
  try {
    const r = await fetch(`/api/tickets/${n}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      /*
       * ⚠️⚠️ 10.09.2026 — HIER STAND `serie_ganz`, UND DAS LOESCHTE DEN TOPF.
       *
       * Martin: *„Ein grosser Fehler ist ... schon vier oder fuenf im Topf
       * ... ich einmal draufklicke: Der ganze Topf ist weg."* Nachgemessen
       * zu genau diesem Satz: Ticket 2204 („5 bis 10 Klimmzuege", serie_zahl
       * 4) ging um 19:57:34 Ortszeit in EINEM Schritt auf `resolved`.
       *
       * Ein Haken zaehlt den Stapel um eins herunter; die Zeile bleibt und
       * quittiert („Abgehakt — noch 3 offen"). Begruendung ausfuehrlich in
       * frontend/lib/tickets-view.ts bei `erledigeTicket`.
       */
      body: JSON.stringify({ status }),
    });
    if (!r.ok) return false;
    /*
     * 06.09.2026 (Martin: „wenn Klimmzüge gestapelt werden und ich drücke
     * einmal auf bestätigen, dann sind alle weg"): Ein Haken zaehlt einen
     * Stapel nur herunter — das Ticket bleibt dann offen und die Zeile darf
     * nicht verschwinden. Der Server sagt es in seiner Antwort.
     */
    try {
      const j = (await r.json()) as { ticket?: { status?: string; serieZahl?: number | null } };
      const st = j?.ticket?.status;
      /*
       * ⚠️ 09.09.2026 — hier stand nur „teil". Die Zahl war da und wurde
       *    weggeworfen; die Oberflaeche konnte deshalb nicht sagen, WIE VIEL
       *    der Haken bewirkt hat. Genau daran ist Martin haengengeblieben:
       *    *„Es wurde nicht als abgegolten quittiert."* Sein Haken KAM an
       *    (11:07:00, HTTP 200) und zaehlte den Stapel von 2 auf 1 — nur sah
       *    er davon nichts, weil die Zeile ohne ihr „2×" einfach dastand.
       */
      if (st === "open" || st === "in_progress") {
        return { rest: Math.max(0, j?.ticket?.serieZahl ?? 0) };
      }
    } catch {
      /* ohne Antwort bleibt es beim einfachen Ja */
    }
    return true;
  } catch {
    return false;
  }
}

const ABRUF_MS = 6000;

/** Abruf mit Frist — sonst bleibt Zuhause auf dem grauen Gerüst stehen. */

function mitZeit<T>(p: Promise<T>, fallback: T): Promise<T> {
  return new Promise((resolve) => {
    const t = window.setTimeout(() => resolve(fallback), ABRUF_MS);
    p.then(
      (v) => {
        window.clearTimeout(t);
        resolve(v);
      },
      () => {
        window.clearTimeout(t);
        resolve(fallback);
      },
    );
  });
}

function leerSatz(text: string): HTMLParagraphElement {
  const p = document.createElement("p");
  p.className = "zuhause-leer-satz";
  p.textContent = text;
  return p;
}

/** Eine Zeile im Gruss — Tageszeit oben, Name darunter. */
function zeileSpan(text: string, klasse?: string): HTMLSpanElement {
  const el = document.createElement("span");
  el.className = klasse ? `zuhause-gruss-zeile ${klasse}` : "zuhause-gruss-zeile";
  el.textContent = text;
  return el;
}

/**
 * Eine Zuhause-Zeile — Zeichen, Titel, Nebenzeile.
 * DRY: Heute, Offen und Zuletzt bauen hier, nicht jede für sich.
 */
function baueZeile(o: {
  zeichen: string;
  titel: string;
  neben?: string | null;
  klassen?: string[];
  beiKlick: () => void;
}): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = ["zuhause-zeile", ...(o.klassen ?? [])].join(" ");

  const zeichen = document.createElement("span");
  zeichen.className = "zuhause-zeile-zeichen";
  zeichen.innerHTML = zeichenMarkup(o.zeichen);
  btn.append(zeichen);

  const text = document.createElement("span");
  text.className = "zuhause-zeile-text";
  const titel = document.createElement("span");
  titel.className = "zuhause-zeile-titel";
  titel.textContent = o.titel;
  text.append(titel);
  if (o.neben) {
    const neben = document.createElement("span");
    neben.className = "zuhause-zeile-datum";
    neben.textContent = o.neben;
    text.append(neben);
  }
  btn.append(text);
  btn.addEventListener("click", o.beiKlick);
  return btn;
}

/** Welches Zeichen eine offene Zeile traegt — Faden, Mail, Erinnerung, Aufgabe. */
function zeichenFuerOffen(z: ZuhauseZeile & { kind?: string | null }): string {
  // Der Spiegel bekommt den Mond — er gehoert zum abgeschlossenen Tag.
  if (z.kind === "spiegel") return "mond";
  if (z.kind === "frage") return "faden";
  if (istOffenFaden(z)) return "faden";
  const kind = (z.kind || "").toLowerCase();
  if (/mail|e-mail|posteingang|gmail/i.test(z.titel)) return "mail";
  // 2026-08-25: "aufgabe" war ein Haken — direkt neben dem Haken-KNOPF am
  // rechten Rand. Zweimal dasselbe Zeichen in einer Zeile sagt nichts. Alles
  // auf dieser Flaeche wartet ohnehin darauf, erledigt zu werden; die Uhr
  // sagt, was zutrifft: es liegt und wartet.
  if (kind === "notification" || kind === "reminder" || !kind) return "erinnerung";
  return "aufgabe";
}

/**
 * Was aussteht, ohne ein Ticket zu sein (2026-08-29).
 *
 * ⚠️ Faellt der Endpunkt aus, kommt eine leere Liste — Zuhause zeigt dann
 * eben nur die Tickets. Eine Uebersicht darf nie die Seite mitreissen.
 */
async function holeOffeneSachen(): Promise<
  Array<{
    id: string;
    titel: string;
    schluessel: string;
    art?: string;
    /** Wie lange steht das schon aus (2026-09-05). */
    alterTage?: number;
  }>
> {
  const r = await fetch("/api/offen", { credentials: "include" });
  if (!r.ok) return [];
  const j = (await r.json()) as {
    ok?: boolean;
    sachen?: Array<{
      id?: unknown;
      titel?: unknown;
      schluessel?: unknown;
      art?: unknown;
      alterTage?: unknown;
    }>;
  };
  if (!j.ok || !Array.isArray(j.sachen)) return [];
  return j.sachen.flatMap((s) =>
    typeof s.id === "string" &&
    typeof s.titel === "string" &&
    typeof s.schluessel === "string"
      ? [
          {
            id: s.id,
            titel: s.titel,
            schluessel: s.schluessel,
            art: typeof s.art === "string" ? s.art : undefined,
            alterTage:
              typeof s.alterTage === "number" ? s.alterTage : undefined,
          },
        ]
      : [],
  );
}

async function holeOffeneTickets(): Promise<
  Array<{
    id: number;
    title: string;
    kind?: string | null;
    createdAt?: string | null;
    /** Wie oft diese wiederkehrende Meldung schon anstand (2026-09-05). */
    serieZahl?: number | null;
  }>
> {
  const r = await fetch("/api/tickets?status=open", { credentials: "include" });
  if (!r.ok) return [];
  const j = (await r.json()) as {
    tickets?: Array<{
      id: number;
      title: string;
      kind?: string | null;
      createdAt?: string | null;
      serieZahl?: number | null;
    }>;
  };
  const alle = Array.isArray(j.tickets) ? j.tickets : [];
  /*
   * ⚠️ WIEDERHERGESTELLT am 29.08.2026.
   *
   *   Martin: „Was sind das für Sachen? Vorschlag? Wiederkehrender Fehler?
   *   Was soll ich damit anfangen?"
   *
   *   Auf seinem Zuhause standen Entwickler-Tickets zwischen seinen E-Mails:
   *   „Vorschlag: Wiederkehrender Fehler", „Dienst ausgefallen:
   *   voice-v8-agent.service". Das sind Selbstbefunde des Systems — Zettel an
   *   MICH, nicht an ihn.
   *
   *   Der Filter dafuer war da und heisst `istZuhauseTun` („Nur menschliche
   *   Offene — Faeden und Meldungen, keine Entwickler-Tickets"). Er ist am
   *   20.08. verschwunden, und zwar als BEIFANG: Commit 12d7518 hiess
   *   „fix(erinnerungen): abgeschnittener Platzhalter im Suchfeld" und hat
   *   nebenbei `alle.filter(istZuhauseTun)` durch `alle` ersetzt. Neun Tage
   *   lang sammelten sich System-Zettel in Martins Liste.
   *
   *   Gemessen am 29.08.: 7 notification (seine E-Mails, die tiefe Frage)
   *   gegen 6 ticket (Vorschlaege, Dienstausfaelle, Fehlerberichte). Der
   *   Filter trennt genau dort.
   *
   *   ⚠️ Die Tickets werden nicht geloescht — sie stehen weiter unter
   *   „Aufgaben", wo sie hingehoeren.
   */
  // teileOffene legt Fäden nach vorn — wartende Punkte zuerst auf dem Telefon.
  return teileOffene(alle.filter(istZuhauseTun)).liste;
}

/**
 * Das Wetter fuer die Meta-Zeile.
 *
 * 2026-08-29 (Martin: „wäre gut, wenn auch dort das Wetter angezeigt wird vom
 * heutigen Tag, bezogen auf den jeweiligen Standort"): Vorher stand hier die
 * Kurzfassung des AUGENBLICKS — „Austraße 33, Hollenthon, 17°, leichter
 * Regen". Lang, und sie sagte nichts ueber den Tag.
 *
 * Jetzt: Ortsname kurz, dann der TAG. Aus
 *   „Austraße 33, Hollenthon, 17°, leichter Regen"
 * wird
 *   „Hollenthon · 17°, später 25°, leichter Regen".
 *
 * ⚠️ Faellt die Vorhersage aus, bleibt die alte Kurzfassung stehen — lieber
 * die Minute als gar kein Wetter.
 */
/** Was Zuhause vom Wetter braucht — als Satz UND in Teilen. */
export interface WetterBlick {
  /** Fertige Zeile fuer die Meta-Zeile im Blatt-Stil. */
  zeile: string;
  /** Fuer die Kachel: Zahl und Wortlaut getrennt. */
  jetzt: number | null;
  spaeter: string | null;
  beschreibung: string | null;
  ort: string;
  /**
   * Sonnenauf- und -untergang von heute, oertliche Zeit. Entscheidet die
   * Tageszeit der Szene — feste Stunden wussten nicht, dass die Sonne Ende
   * August schon um 19:50 untergeht.
   */
  sonne: { aufgang: string; untergang: string } | null;
  /**
   * Bewölkung in Prozent (0–100). Bestimmt, WIE VIELE Wolken am Himmel
   * stehen — der Wettercode sagt nur „bedeckt" oder „heiter".
   * (05.09.2026, Martins Frage.)
   */
  bewoelkung: number | null;
  /**
   * Niederschlag des Tages in Millimetern. Bestimmt, WIE STARK der Regen in
   * der Szene faellt. (11.09.2026, Martins Frage — die Zahl lag schon in
   * derselben Antwort und wurde nur nie abgefragt.)
   */
  regenMm: number | null;
}

async function holeWetterJetzt(): Promise<WetterBlick | null> {
  const r = await fetch("/api/weather/now", { credentials: "include" });
  if (!r.ok) return null;
  const j = (await r.json()) as {
    ok?: boolean;
    kurz?: string;
    ort?: string | null;
    sonne?: { aufgang?: string; untergang?: string } | null;
    bewoelkung?: number | null;
    heute?: {
      zeile?: string;
      jetzt?: number;
      max?: number;
      min?: number;
      beschreibung?: string;
      regenMm?: number;
    } | null;
  };
  if (!j.ok) return null;
  const ort = kurzerOrt(j.ort);
  // Nur als Paar brauchbar — ein halber Wert waere schlimmer als keiner.
  const sonne =
    typeof j.sonne?.aufgang === "string" && typeof j.sonne?.untergang === "string"
      ? { aufgang: j.sonne.aufgang, untergang: j.sonne.untergang }
      : null;
  const h = j.heute;
  const zeileRoh = h?.zeile?.trim();
  if (!zeileRoh) {
    const kurz = typeof j.kurz === "string" && j.kurz.trim() ? j.kurz : null;
    return kurz
      ? {
          zeile: kurz,
          jetzt: null,
          spaeter: null,
          beschreibung: null,
          ort,
          sonne,
          bewoelkung: typeof j.bewoelkung === "number" ? j.bewoelkung : null,
          regenMm: typeof h?.regenMm === "number" ? h.regenMm : null,
        }
      : null;
  }
  // Die Kachel zeigt „später 25°" bzw. „bis 17°" — dieselbe Entscheidung wie
  // in der Zeile, deshalb hier aus denselben Zahlen abgeleitet.
  const jetzt = typeof h?.jetzt === "number" ? h.jetzt : null;
  const max = typeof h?.max === "number" ? h.max : null;
  const min = typeof h?.min === "number" ? h.min : null;
  const spaeter =
    jetzt !== null && max !== null && min !== null
      ? max > jetzt
        ? `später ${max}°`
        : `bis ${min}°`
      : null;
  return {
    zeile: ort ? `${ort} · ${zeileRoh}` : zeileRoh,
    jetzt,
    spaeter,
    beschreibung: h?.beschreibung?.trim() || null,
    ort,
    sonne,
    bewoelkung: typeof j.bewoelkung === "number" ? j.bewoelkung : null,
    regenMm: typeof h?.regenMm === "number" ? h.regenMm : null,
  };
}

/**
 * „Austraße 33, Hollenthon" → „Hollenthon".
 *
 * Der gespeicherte Standort ist eine volle Adresse aus der Rueckwaerts-Suche.
 * In einer Meta-Zeile neben der Uhr ist die Hausnummer nur Laerm; der Ort
 * beantwortet die eigentliche Frage („ist das mein Wetter?").
 */
function kurzerOrt(ort?: string | null): string {
  const teile = (ort ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return teile[teile.length - 1] ?? "";
}

/** „Gestern" → „gestern" mitten im Satz; ein Datum („5. Sept.") bleibt. */
function kleinAnfang(wort: string): string {
  return /^[A-ZÄÖÜ][a-zäöü]/.test(wort) ? wort[0].toLowerCase() + wort.slice(1) : wort;
}
